import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  Box,
  Button,
  CircularProgress,
  Fade,
  IconButton,
  Modal,
  Stack,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";

import LayerdIcon from "../../../common/LayerdIcon";
import { SafeTextField, GreenSwitch } from "../../../common/Greens";
import { containedButtonSx, outlinedButtonSx } from "../../../common/Button";
import { UI_ICONS } from "../../../../icons/uiLucideIcons";
import { ReactComponent as ArrowrightIcon } from "../../../../vendor/_new/icon/sites/arrow_right.svg";
import { ReactComponent as ArrowleftIcon } from "../../../../vendor/_new/icon/sites/arrow_left.svg";
import { ReactComponent as InfoIcon } from "../../../../vendor/_new/icon/sites/info.svg";
import { ReactComponent as InsecureIcon } from "../../../../vendor/_new/icon/sites/insecure.svg";
import { ReactComponent as AdaptiveIcon } from "../../../../vendor/_new/icon/sites/adaptive.svg";
import { ReactComponent as Adaptivee2eIcon } from "../../../../vendor/_new/icon/sites/adaptive_e2e.svg";
import { ReactComponent as Advancede2eIcon } from "../../../../vendor/_new/icon/sites/advanced_e2e.svg";
import { ReactComponent as UploadIcon } from "../../../../vendor/_new/icon/sites/upload.svg";
import { ReactComponent as DoneIcon } from "../../../../vendor/_new/icon/sites/done.svg";
import { ReactComponent as SignaturewafIcon } from "../../../../vendor/_new/icon/sites/signature_waf.svg";
import { ReactComponent as GlobalIcon } from "../../../../vendor/_new/icon/services/global.svg";
import { ReactComponent as WebIcon } from "../../../../vendor/_new/icon/sites/www.svg";
import { ReactComponent as ServerIcon } from "../../../../vendor/_new/icon/sites/origin.svg";
import { ReactComponent as CertIcon } from "../../../../vendor/_new/icon/sites/check_rounded.svg";
import { ReactComponent as RedirectIcon } from "../../../../vendor/_new/icon/sites/next_line.svg";
import { Check as LucideCheckIcon, Copy as LucideCopyIcon } from "lucide-react";
import DnsOutlinedIcon from "@mui/icons-material/DnsOutlined";
import LinkOutlinedIcon from "@mui/icons-material/LinkOutlined";
import colors from "../../../../utils/colors";
import { SslType, CertificateType, UserType } from "../../../../utils/constants";
import { useNotification } from "../../../../contexts/NotificationContext";
import useSite from "../../../../hooks/user/useSite";
import useHome from "../../../../hooks/user/useHome";
import useSSLConfig from "../../../../hooks/user/useSSLConfig";
import useWAFConfig from "../../../../hooks/user/useWAFConfig";
import useAuth from "../../../../hooks/useAuth";
import usePermissions from "../../../../hooks/usePermissions";
import UnsavedChangesConfirmModal from "../super/design/UnsavedChangesConfirmModal";
import { validateAddDomainOrigin, validateAddDomainWebsite } from "./addDomainValidators";
import {
  getInstallContinueBlockReason,
  getUploadPemBlockReason,
  hasCnameValidationRecords,
  isConfigProcessFetchFailed,
  isSslCertificateReady,
  shouldPersistHttpsRedirect,
  shouldSkipInstallForInsecure,
  isOriginCertificateReady,
  persistableWizardStep,
  resolveResumeWizardStepAfterHydrate,
  shouldPersistWizardStepOnClose,
  shouldPersistWizardStepOnNavigate,
} from "./addDomain2Gates";
import AddSiteProjectSelect from "./AddSiteProjectSelect";

const TIP_IDLE =
  "Layerd will perform a health check of the website and origin server before accepting the registration. Ensure that the domain is active and the origin server is reachable before adding the site to Layerd.";
const TIP_WEBSITE =
  "Enter the website domain name without 'www'. Layerd provides the 'www' subdomain by default, so there's no need to configure it separately.";
const TIP_ORIGIN = "Provide the actual origin IP address where the clean requests need to be sent.";

/** SSL mode tips — update when the selected mode card changes. */
const SSL_TIPS_BY_MODE = {
  [SslType.OFF]: [
    "InSecure applies no encryption between the browser, Layerd AI, and your origin.",
    "Certificate install is skipped for this mode. Continue directly to WAF configuration.",
  ],
  [SslType.FLEXIBLE]: [
    "Adaptive encrypts traffic between the browser and Layerd AI. Origin traffic is not encrypted by this mode.",
    "Next you will install a certificate — Universal, Upload, or Generate Origin.",
  ],
  [SslType.FULL]: [
    "Adaptive E2E encrypts end-to-end using a self-signed certificate on your origin server.",
    "Ensure you can install or generate an origin certificate on the next step before continuing.",
  ],
  [SslType.FULL_STRICT]: [
    "Advanced E2E requires a trusted CA or Layerd AI Origin CA certificate on your server.",
    "On the next step, prefer Universal or Generate Origin for a managed or origin certificate.",
  ],
};

/** Install SSL method tips — update when Universal / Upload / Generate changes. */
const INSTALL_TIPS_BY_CERT = {
  free: [
    "Universal issues a managed certificate for your domain via DNS validation.",
    "Add the CNAME record in your DNS manager, then verify before continuing to WAF.",
  ],
  upload: [
    "Upload your own CA-signed fullchain and private key to secure the domain.",
    "Paste or upload both PEM files, then continue — invalid or mismatched keys will be rejected.",
  ],
  generate: [
    "Generate Origin creates a certificate for strict encryption between your server and Layerd AI.",
    "Copy the certificate and key to your origin server after generation.",
  ],
};

const WAF_TIP_LAYERED = "These are layered (Defence in depth) and will not conflict.";

const CONFIG_PROCESS_STEPS = [
  "Register site in Layerd AI",
  "Generate / Upload SSL certificates",
  "Install SSL certificates",
  "Enable HTTP to HTTPS redirection",
  "Enable Layerd Signature WAF",
  "Final Review",
];

/** Classic ConfigurationProcess.js status values. */
const ConfigStepStatus = { PENDING: "pending", SKIPPED: "skipped", DONE: "done", ERROR: "error", ACTIVE: "active" };

const PROGRESS_SEGMENTS = 6;
const FIELD_BORDER = colors.neutral[200];
const TEXT_PRIMARY = "#292929";
const SLIDE_MS = 300;
const SLIDE_PX = 32;
const SLIDE_EASE = "cubic-bezier(0.4, 0, 0.2, 1)";
/** Body slot floor so WI / SSL / Install keep a similar modal size. Applied on the paper, not the scroller. */
const STEP_BODY_MIN_HEIGHT = 558;
/** Header + progress + paddings + footer around the body slot. */
const MODAL_CHROME_PX = 206;
const WIZARD_STEP_COUNT = 6;

/** Scrollbars hidden until hover (self or parent with `&:hover [data-hover-scroll]`). */
const hoverScrollbarSx = {
  scrollbarWidth: "none",
  scrollbarColor: "transparent transparent",
  "&::-webkit-scrollbar": { width: 0, height: 0 },
  "&::-webkit-scrollbar-track": { backgroundColor: "transparent" },
  "&::-webkit-scrollbar-thumb": {
    backgroundColor: "transparent",
    borderRadius: 8,
  },
  "&:hover": {
    scrollbarWidth: "thin",
    scrollbarColor: `${colors.neutral[300]} transparent`,
  },
  "&:hover::-webkit-scrollbar": { width: 6, height: 6 },
  "&:hover::-webkit-scrollbar-thumb": {
    backgroundColor: colors.neutral[300],
  },
};

/** Parent hover reveals nested `[data-hover-scroll]` scrollbars (e.g. Install SSL right panel). */
const revealChildScrollbarsOnHoverSx = {
  "&:hover [data-hover-scroll]": {
    scrollbarWidth: "thin",
    scrollbarColor: `${colors.neutral[300]} transparent`,
    "&::-webkit-scrollbar": { width: 6, height: 6 },
    "&::-webkit-scrollbar-thumb": {
      backgroundColor: colors.neutral[300],
      borderRadius: 8,
    },
  },
};

const CERT_INSTALL_OPTIONS = [
  {
    value: "free",
    title: "Universal",
    description: "Issue a managed certificate for your domain.",
  },
  {
    value: "upload",
    title: "Upload SSL Certificate",
    description: "Provide your own CA-signed certificate and key.",
  },
  {
    value: "generate",
    title: "Generate Origin Certificate",
    description: "Create an origin certificate for strict end-to-end encryption.",
  },
];

/** Finalize step DNS + tip copy (classic Finalize / Figma). */
const REVIEW_TIP_WWW_CNAME =
  "Create or update the CNAME record for your www subdomain to the provided CNAME record.";
const REVIEW_TIP_MULTI_SUBDOMAIN =
  "If you are going to use multiple subdomains for your website, please create or update the CNAME records for all of the subdomains individually after onboarding to Layerd AI.";

const SSL_TYPES = [
  {
    type: SslType.OFF,
    title: "InSecure",
    description: "No encryption applied.",
    label: "Least secured",
  },
  {
    type: SslType.FLEXIBLE,
    title: "Adaptive",
    description: "Encrypts traffic between the browser and Layerd AI.",
    label: "Medium security",
  },
  {
    type: SslType.FULL,
    title: "Adaptive E2E",
    description: "Encrypts end-to-end, using a self signed certificate on the server.",
    label: "Secured",
  },
  {
    type: SslType.FULL_STRICT,
    title: "Advanced E2E",
    description:
      "Encrypts end-to-end, but requires a trusted CA or Layerd AI Origin CA certificate on the server.",
    label: "Most secured",
  },
];

const SSL_MODE_META = {
  [SslType.OFF]: {
    iconWell: colors.red[10],
    meterColor: colors.red[200],
    filledTracks: 1,
    Icon: InsecureIcon,
  },
  [SslType.FLEXIBLE]: {
    iconWell: colors.yellow[10],
    meterColor: colors.yellow[200],
    filledTracks: 2,
    Icon: AdaptiveIcon,
  },
  [SslType.FULL]: {
    iconWell: colors.primary[10],
    meterColor: colors.primary[300],
    filledTracks: 3,
    Icon: Adaptivee2eIcon,
  },
  [SslType.FULL_STRICT]: {
    iconWell: colors.green[10],
    meterColor: colors.secondary[400],
    filledTracks: 4,
    Icon: Advancede2eIcon,
  },
};

function ProgressBar({ activeStep = 0 }) {
  return (
    <Box sx={{ display: "flex", gap: "4px", width: "100%", alignItems: "center" }}>
      {Array.from({ length: PROGRESS_SEGMENTS }).map((_, index) => (
        <Box
          key={`seg_${index}`}
          sx={{
            flex: 1,
            height: 6,
            minWidth: 0,
            borderRadius: "20px",
            backgroundColor: colors.neutral[300],
            overflow: "hidden",
            position: "relative",
          }}
        >
          {index <= activeStep && (
            <Box
              sx={{
                position: "absolute",
                left: 0,
                top: 0,
                height: 6,
                width: "100%",
                borderRadius: "20px",
                backgroundColor: colors.primary[400],
              }}
            />
          )}
        </Box>
      ))}
    </Box>
  );
}

function TipBanner({ text }) {
  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        gap: "12px",
        width: "100%",
        minWidth: 0,
        maxWidth: "100%",
        px: "12px",
        py: "8px",
        backgroundColor: colors.primary[10],
        border: `1px solid ${colors.primary[200]}`,
        borderRadius: "5px",
        boxSizing: "border-box",
      }}
    >
      <InfoIcon style={{ width: 16, height: 16, color: colors.primary[300], flexShrink: 0 }} />
      <Typography
        sx={{
          flex: 1,
          minWidth: 0,
          fontSize: 12,
          lineHeight: "18px",
          fontWeight: 400,
          color: TEXT_PRIMARY,
          overflowWrap: "anywhere",
        }}
      >
        {text}
      </Typography>
    </Box>
  );
}

function SecurityMeter({ filledTracks, color }) {
  return (
    <Box sx={{ display: "flex", gap: "8px", width: "100%", height: 2, alignItems: "flex-start" }}>
      {Array.from({ length: 4 }).map((_, index) => (
        <Box
          key={`meter_${index}`}
          sx={{
            flex: 1,
            height: 2,
            minWidth: 0,
            borderRadius: "20px",
            backgroundColor: colors.neutral[200],
            overflow: "hidden",
            position: "relative",
          }}
        >
          {index < filledTracks && (
            <Box
              sx={{
                position: "absolute",
                left: 0,
                top: 0,
                height: 2,
                width: "100%",
                borderRadius: "20px",
                backgroundColor: color,
              }}
            />
          )}
        </Box>
      ))}
    </Box>
  );
}

function SslModeRadio({ selected }) {
  return (
    <Box
      sx={{
        width: 24,
        height: 24,
        borderRadius: "799px",
        border: `2px solid ${selected ? colors.primary[300] : colors.neutral[300]}`,
        boxSizing: "border-box",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        flexShrink: 0,
        overflow: "hidden",
      }}
    >
      {selected ? (
        <Box
          sx={{
            width: 17.6,
            height: 17.6,
            borderRadius: "799px",
            backgroundColor: colors.primary[300],
          }}
        />
      ) : null}
    </Box>
  );
}

function SslModeCard({ item, selected, onSelect, disabled = false }) {
  const meta = SSL_MODE_META[item.type] || SSL_MODE_META[SslType.OFF];
  const Icon = meta.Icon;

  const activate = () => {
    if (disabled) return;
    onSelect(item.type);
  };

  return (
    <Box
      role="radio"
      aria-checked={selected}
      aria-disabled={disabled}
      tabIndex={disabled ? -1 : 0}
      onClick={activate}
      onKeyDown={(e) => {
        if (disabled) return;
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          activate();
        }
      }}
      sx={{
        flex: 1,
        minWidth: 0,
        height: "100%",
        display: "flex",
        flexDirection: "column",
        gap: "12px",
        justifyContent: "center",
        p: "16px",
        boxSizing: "border-box",
        cursor: disabled ? "not-allowed" : "pointer",
        opacity: disabled ? 0.6 : 1,
        borderRadius: "12px",
        backgroundColor: selected ? colors.primary[100] : colors.neutral[100],
        border: `1px solid ${selected ? colors.primary[300] : FIELD_BORDER}`,
      }}
    >
      <Box sx={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", width: "100%", gap: 1 }}>
        <Box sx={{ display: "flex", flexDirection: "column", gap: "12px", flex: 1, minWidth: 0 }}>
          <Box
            sx={{
              width: "fit-content",
              p: "12px",
              borderRadius: "4px",
              backgroundColor: meta.iconWell,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <Icon style={{ width: 16, height: 16, color: TEXT_PRIMARY }} />
          </Box>
          <Box sx={{ width: "100%" }}>
            <Typography sx={{ fontSize: 16, lineHeight: "18px", fontWeight: 500, color: TEXT_PRIMARY }}>
              {item.title}
            </Typography>
            <Typography sx={{ fontSize: 12, lineHeight: "18px", fontWeight: 400, color: TEXT_PRIMARY, mt: "4px" }}>
              {item.description}
            </Typography>
          </Box>
          <Box
            sx={{
              width: "100%",
              px: "8px",
              py: "4px",
              borderRadius: "12px",
              backgroundColor: colors.primary[100],
              border: `1px solid ${colors.primary[300]}`,
              boxSizing: "border-box",
            }}
          >
            <Typography sx={{ fontSize: 10, lineHeight: "14px", fontWeight: 500, color: colors.primary[300] }}>
              {item.label}
            </Typography>
          </Box>
        </Box>
        <SslModeRadio selected={selected} />
      </Box>
      <SecurityMeter filledTracks={meta.filledTracks} color={meta.meterColor} />
    </Box>
  );
}

function WafStatusBadge({ enabled }) {
  return (
    <Box
      sx={{
        display: "inline-flex",
        alignItems: "center",
        gap: "6px",
        px: "8px",
        py: "4px",
        borderRadius: "12px",
        backgroundColor: enabled ? colors.green[10] : colors.neutral[100],
        border: `1px solid ${enabled ? colors.green[200] : colors.neutral[300]}`,
        boxSizing: "border-box",
      }}
    >
      <Box
        sx={{
          width: 6,
          height: 6,
          borderRadius: "50%",
          backgroundColor: enabled ? colors.green[300] : colors.neutral[500],
          flexShrink: 0,
        }}
      />
      <Typography
        sx={{
          fontSize: 12,
          lineHeight: "18px",
          fontWeight: 500,
          color: enabled ? colors.green[300] : colors.neutral[600],
        }}
      >
        {enabled ? "Enabled" : "Disabled"}
      </Typography>
    </Box>
  );
}

function WafToggleCard({ title, description, enabled, onToggle, Icon, disabled = false }) {
  return (
    <Box
      sx={{
        flex: 1,
        minWidth: 0,
        height: 154,
        p: "16px",
        boxSizing: "border-box",
        borderRadius: "12px",
        border: `1px solid ${enabled ? colors.primary[300] : FIELD_BORDER}`,
        backgroundColor: enabled ? colors.primary[100] : colors.neutral[100],
        display: "flex",
        flexDirection: "column",
        justifyContent: "space-between",
        gap: "12px",
        opacity: disabled ? 0.6 : 1,
      }}
    >
      <Box sx={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: "12px" }}>
        <Box sx={{ display: "flex", alignItems: "flex-start", gap: "12px", minWidth: 0, flex: 1 }}>
          <Icon style={{ width: 40, height: 40, color: colors.primary[300], flexShrink: 0 }} />
          <Box sx={{ minWidth: 0 }}>
            <Typography sx={{ fontSize: 16, lineHeight: "24px", fontWeight: 500, color: TEXT_PRIMARY }}>
              {title}
            </Typography>
            <Typography sx={{ fontSize: 12, lineHeight: "18px", fontWeight: 400, color: colors.neutral[700], mt: "2px" }}>
              {description}
            </Typography>
          </Box>
        </Box>
        <GreenSwitch
          checked={enabled}
          onChange={onToggle}
          disabled={disabled}
          inputProps={{ "aria-label": title }}
        />
      </Box>
      <WafStatusBadge enabled={enabled} />
    </Box>
  );
}

/** Classic ConfigurationProcess row — Done / Skipped / Error / In Progress animation. */
function ConfigProcessRow({ index, label, status }) {
  const isDone = status === ConfigStepStatus.DONE;
  const isActive = status === ConfigStepStatus.ACTIVE;
  const isPending = status === ConfigStepStatus.PENDING;
  const isSkipped = status === ConfigStepStatus.SKIPPED;
  const isError = status === ConfigStepStatus.ERROR;

  let borderColor = FIELD_BORDER;
  if (isDone) borderColor = colors.green[200];
  if (isActive) borderColor = colors.primary[300];
  if (isError) borderColor = colors.red[100];

  return (
    <Box
      sx={{
        width: "100%",
        display: "flex",
        alignItems: "center",
        gap: "12px",
        px: "12px",
        py: "12px",
        boxSizing: "border-box",
        borderRadius: "12px",
        border: `1px solid ${borderColor}`,
        backgroundColor: colors.neutral[100],
      }}
    >
      <Box
        sx={{
          width: 28,
          height: 28,
          borderRadius: "50%",
          flexShrink: 0,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          backgroundColor: isActive
            ? colors.primary[300]
            : isError
              ? colors.red[100]
              : isDone
                ? colors.green[10]
                : colors.neutral[400],
          border: isDone || isSkipped || isPending ? `2px solid ${isDone ? colors.green[300] : colors.neutral[400]}` : "none",
          boxSizing: "border-box",
        }}
      >
        {isDone ? (
          <Typography sx={{ fontSize: 12, fontWeight: 700, color: colors.green[300], lineHeight: 1 }}>✓</Typography>
        ) : isActive ? (
          <CircularProgress size={14} sx={{ color: "#fff" }} />
        ) : isError ? (
          <Typography sx={{ fontSize: 12, fontWeight: 700, color: "#fff", lineHeight: 1 }}>!</Typography>
        ) : (
          <Typography sx={{ fontSize: 12, fontWeight: 500, color: "#fff", lineHeight: 1 }}>{index + 1}</Typography>
        )}
      </Box>
      <Typography
        sx={{
          flex: 1,
          fontSize: 14,
          lineHeight: "21px",
          fontWeight: isDone || isActive ? 500 : 400,
          color: isDone
            ? colors.green[300]
            : isActive
              ? colors.primary[300]
              : isError
                ? colors.red[100]
                : colors.neutral[600],
        }}
      >
        {label}
      </Typography>
      <Typography
        sx={{
          fontSize: 12,
          lineHeight: "18px",
          color: isDone
            ? colors.green[300]
            : isError
              ? colors.red[100]
              : isActive
                ? colors.primary[300]
                : colors.neutral[500],
          flexShrink: 0,
        }}
      >
        {isDone ? "Done" : isSkipped ? "Skipped" : isError ? "Error" : isActive ? "In Progress" : ""}
      </Typography>
    </Box>
  );
}

/** LE-444 Review — Figma Review Configuration row cell (label left, value/badge right). */
function ReviewFieldCell({ icon, label, children, fullWidth = false }) {
  return (
    <Box
      sx={{
        flex: fullWidth ? "1 1 100%" : { xs: "1 1 100%", sm: "1 1 calc(50% - 6px)" },
        minWidth: 0,
        width: fullWidth ? "100%" : undefined,
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        gap: "12px",
        p: "12px",
        boxSizing: "border-box",
        borderRadius: "12px",
        border: `1px solid ${FIELD_BORDER}`,
        backgroundColor: colors.neutral[100],
      }}
    >
      <Box sx={{ display: "flex", alignItems: "center", gap: "8px", minWidth: 0, flex: 1 }}>
        {icon}
        <Typography
          sx={{
            fontSize: 14,
            lineHeight: "21px",
            fontWeight: 500,
            color: TEXT_PRIMARY,
          }}
        >
          {label}
        </Typography>
      </Box>
      <Box sx={{ display: "flex", alignItems: "center", gap: "8px", flexShrink: 0, maxWidth: "55%", minWidth: 0 }}>
        {children}
      </Box>
    </Box>
  );
}

function ReviewTextValue({ value }) {
  return (
    <Typography
      sx={{
        fontSize: 14,
        lineHeight: "21px",
        fontWeight: 500,
        color: colors.primary[300],
        textAlign: "right",
        wordBreak: "break-word",
      }}
    >
      {value || "—"}
    </Typography>
  );
}

/** Copy + Check stacked so show/off can fade without jumping the control. */
function Ad2CopyButton({ ariaLabel, onClick, disabled = false, copied = false }) {
  const showCopied = copied && !disabled;
  const glyphMotionSx = {
    position: "absolute",
    inset: 0,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    pointerEvents: "none",
    "@media (prefers-reduced-motion: reduce)": {
      transition: "none",
      transform: "none",
    },
  };

  return (
    <IconButton
      aria-label={ariaLabel}
      size="small"
      disableRipple
      onClick={onClick}
      disabled={disabled}
      sx={{
        p: 0.25,
        borderRadius: "6px",
        border: "1px solid",
        borderColor: showCopied ? colors.green[200] : "rgba(31, 193, 107, 0)",
        backgroundColor: showCopied ? colors.green[10] : "rgba(31, 193, 107, 0)",
        color: showCopied ? colors.green[200] : colors.neutral[600],
        flexShrink: 0,
        transition: "border-color 0.16s ease, background-color 0.16s ease, color 0.16s ease",
        "& .ad2-copy-glyph": {
          transition: "opacity 0.16s ease, transform 0.16s ease",
        },
        "&:hover": {
          color: showCopied ? colors.green[200] : colors.primary[300],
          backgroundColor: showCopied ? colors.green[10] : "rgba(31, 193, 107, 0)",
          borderColor: showCopied ? colors.green[200] : "rgba(31, 193, 107, 0)",
        },
        "&.Mui-disabled": {
          color: colors.neutral[400],
          borderColor: "rgba(31, 193, 107, 0)",
          backgroundColor: "rgba(31, 193, 107, 0)",
        },
        "@media (prefers-reduced-motion: reduce)": {
          transition: "none",
          "& .ad2-copy-glyph": { transition: "none", transform: "none" },
        },
      }}
    >
      <Box sx={{ position: "relative", width: 16, height: 16 }}>
        <Box
          className="ad2-copy-glyph"
          aria-hidden
          sx={{
            ...glyphMotionSx,
            opacity: showCopied ? 0 : 1,
            transform: showCopied ? "scale(0.72)" : "scale(1)",
          }}
        >
          <LayerdIcon icon={LucideCopyIcon} size={16} color="neutral" strokeWidth={1.75} />
        </Box>
        <Box
          className="ad2-copy-glyph"
          aria-hidden
          sx={{
            ...glyphMotionSx,
            color: colors.green[200],
            opacity: showCopied ? 1 : 0,
            transform: showCopied ? "scale(1)" : "scale(0.72)",
          }}
        >
          <LucideCheckIcon size={16} strokeWidth={2.2} />
        </Box>
      </Box>
    </IconButton>
  );
}

function ReviewCopyValue({ value, ariaLabel, loading = false }) {
  const { showSuccess } = useNotification();
  const [copied, setCopied] = useState(false);
  const copiedTimerRef = useRef(null);

  useEffect(() => {
    return () => {
      if (copiedTimerRef.current) window.clearTimeout(copiedTimerRef.current);
    };
  }, []);

  useEffect(() => {
    setCopied(false);
    if (copiedTimerRef.current) window.clearTimeout(copiedTimerRef.current);
  }, [value]);

  const handleCopy = async () => {
    if (!value || loading) return;
    try {
      await navigator.clipboard.writeText(String(value));
      if (copiedTimerRef.current) window.clearTimeout(copiedTimerRef.current);
      setCopied(true);
      showSuccess("Copied to clipboard");
      copiedTimerRef.current = window.setTimeout(() => setCopied(false), 1200);
    } catch {
      /* ignore */
    }
  };

  const display = loading ? "Loading..." : value || "—";

  return (
    <Box sx={{ display: "flex", alignItems: "center", gap: "8px", minWidth: 0 }}>
      <Typography
        sx={{
          fontSize: 14,
          lineHeight: "21px",
          fontWeight: 500,
          color: loading ? colors.neutral[500] : colors.primary[300],
          textAlign: "right",
          wordBreak: "break-all",
        }}
      >
        {display}
      </Typography>
      <Ad2CopyButton
        ariaLabel={copied ? "Copied" : ariaLabel}
        onClick={handleCopy}
        disabled={!value || loading}
        copied={copied}
      />
    </Box>
  );
}

function ReviewSvgIcon({ Icon }) {
  return <Icon style={{ width: 20, height: 20, color: colors.primary[300], flexShrink: 0 }} />;
}

function ReviewMuiIcon({ Icon }) {
  return <Icon sx={{ fontSize: 20, color: colors.primary[300], flexShrink: 0 }} />;
}

function ReviewSectionCard({ children }) {
  return (
    <Box
      sx={{
        width: "100%",
        border: `1px solid ${FIELD_BORDER}`,
        borderRadius: "12px",
        p: "12px",
        boxSizing: "border-box",
        backgroundColor: colors.neutral[100],
        display: "flex",
        flexDirection: "column",
        gap: "12px",
      }}
    >
      {children}
    </Box>
  );
}

function ReviewPairRow({ children }) {
  return (
    <Box sx={{ display: "flex", gap: "12px", width: "100%", flexWrap: { xs: "wrap", sm: "nowrap" } }}>
      {children}
    </Box>
  );
}

/** Read-only echo of LE-440 SSL mode on Install SSL step. */
function SslModeSummaryCard({ sslType }) {
  const item = SSL_TYPES.find((t) => t.type === sslType) || SSL_TYPES[0];
  const meta = SSL_MODE_META[item.type] || SSL_MODE_META[SslType.OFF];
  const Icon = meta.Icon;

  return (
    <Box
      sx={{
        width: "100%",
        border: `1px solid ${FIELD_BORDER}`,
        borderRadius: "12px",
        p: "16px",
        boxSizing: "border-box",
        backgroundColor: colors.neutral[100],
        display: "flex",
        flexDirection: "column",
        gap: "12px",
      }}
    >
      <Box
        sx={{
          width: "fit-content",
          p: "12px",
          borderRadius: "4px",
          backgroundColor: meta.iconWell,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <Icon style={{ width: 16, height: 16, color: TEXT_PRIMARY }} />
      </Box>
      <Box>
        <Typography sx={{ fontSize: 16, lineHeight: "18px", fontWeight: 500, color: TEXT_PRIMARY }}>
          {item.title}
        </Typography>
        <Typography sx={{ fontSize: 12, lineHeight: "18px", fontWeight: 400, color: TEXT_PRIMARY, mt: "4px" }}>
          {item.description}
        </Typography>
      </Box>
      <Box
        sx={{
          width: "fit-content",
          px: "8px",
          py: "4px",
          borderRadius: "12px",
          backgroundColor: colors.primary[100],
          border: `1px solid ${colors.primary[300]}`,
          boxSizing: "border-box",
        }}
      >
        <Typography sx={{ fontSize: 10, lineHeight: "14px", fontWeight: 500, color: colors.primary[300] }}>
          {item.label}
        </Typography>
      </Box>
      <SecurityMeter filledTracks={meta.filledTracks} color={meta.meterColor} />
    </Box>
  );
}

function CertInstallOption({ option, selected, onSelect, disabled = false }) {
  const activate = () => {
    if (disabled) return;
    onSelect(option.value);
  };
  return (
    <Box
      role="radio"
      aria-checked={selected}
      aria-disabled={disabled}
      tabIndex={disabled ? -1 : 0}
      onClick={activate}
      onKeyDown={(e) => {
        if (disabled) return;
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          activate();
        }
      }}
      sx={{
        width: "100%",
        display: "flex",
        alignItems: "flex-start",
        justifyContent: "space-between",
        gap: "12px",
        p: "12px",
        boxSizing: "border-box",
        borderRadius: "8px",
        cursor: disabled ? "not-allowed" : "pointer",
        opacity: disabled ? 0.6 : 1,
        backgroundColor: selected ? colors.primary[100] : "transparent",
      }}
    >
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography sx={{ fontSize: 14, lineHeight: "18px", fontWeight: 500, color: TEXT_PRIMARY }}>
          {option.title}
        </Typography>
        <Typography sx={{ fontSize: 12, lineHeight: "18px", fontWeight: 400, color: colors.neutral[700], mt: "4px" }}>
          {option.description}
        </Typography>
      </Box>
      <SslModeRadio selected={selected} />
    </Box>
  );
}

function CopyableField({ label, value, multiline = false, minRows = 1, compact = false }) {
  const [copied, setCopied] = useState(false);
  const copiedTimerRef = useRef(null);

  useEffect(() => {
    return () => {
      if (copiedTimerRef.current) window.clearTimeout(copiedTimerRef.current);
    };
  }, []);

  useEffect(() => {
    setCopied(false);
    if (copiedTimerRef.current) window.clearTimeout(copiedTimerRef.current);
  }, [value]);

  const handleCopy = async () => {
    if (!value) return;
    try {
      await navigator.clipboard.writeText(String(value));
      if (copiedTimerRef.current) window.clearTimeout(copiedTimerRef.current);
      setCopied(true);
      copiedTimerRef.current = window.setTimeout(() => setCopied(false), 1200);
    } catch {
      /* ignore */
    }
  };

  /* Cap tall values; compact = Install SSL side panel (keep left/right heights similar). */
  const valueMaxHeight = multiline ? Math.max(minRows * 18, compact ? 72 : 120) : 54;
  const fieldMinHeight = multiline ? (compact ? 88 : 108) : 65;

  return (
    <Box
      sx={{
        width: "100%",
        border: `1px solid ${FIELD_BORDER}`,
        borderRadius: "12px",
        p: compact ? "10px" : "12px",
        boxSizing: "border-box",
        backgroundColor: colors.neutral[100],
        position: "relative",
        minHeight: fieldMinHeight,
        ...revealChildScrollbarsOnHoverSx,
      }}
    >
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: multiline ? "6px" : 0 }}>
        <Typography sx={{ fontSize: 12, lineHeight: "14px", fontWeight: 500, color: colors.neutral[700] }}>
          {label}
        </Typography>
        <Ad2CopyButton
          ariaLabel={copied ? "Copied" : `Copy ${label}`}
          onClick={handleCopy}
          disabled={!value}
          copied={copied}
        />
      </Box>
      <Typography
        component="pre"
        data-hover-scroll
        sx={{
          m: 0,
          mt: multiline ? 0 : "8px",
          fontSize: 14,
          lineHeight: "18px",
          fontWeight: 400,
          color: value ? TEXT_PRIMARY : colors.neutral[500],
          whiteSpace: "pre-wrap",
          wordBreak: "break-all",
          fontFamily: "inherit",
          minHeight: multiline ? minRows * 18 : undefined,
          maxHeight: valueMaxHeight,
          overflowY: "auto",
          overflowX: "hidden",
          ...hoverScrollbarSx,
        }}
      >
        {value || "—"}
      </Typography>
    </Box>
  );
}

function UploadPemField({ label, value, onChange, onFile, disabled = false }) {
  const inputRef = useRef(null);
  return (
    <Box
      sx={{
        width: "100%",
        border: `1px solid ${FIELD_BORDER}`,
        borderRadius: "12px",
        p: "12px",
        boxSizing: "border-box",
        backgroundColor: colors.neutral[100],
        minHeight: 140,
        maxHeight: 200,
        display: "flex",
        flexDirection: "column",
        gap: "8px",
        opacity: disabled ? 0.6 : 1,
        ...revealChildScrollbarsOnHoverSx,
      }}
    >
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        <Typography sx={{ fontSize: 12, lineHeight: "14px", fontWeight: 500, color: colors.neutral[700] }}>
          {label}
        </Typography>
        <IconButton
          aria-label={`Upload ${label}`}
          size="small"
          disabled={disabled}
          onClick={() => inputRef.current?.click()}
          sx={{ p: 0.25, color: colors.neutral[600] }}
        >
          <UploadIcon style={{ width: 16, height: 16 }} />
        </IconButton>
        <input
          ref={inputRef}
          type="file"
          accept=".pem,.crt,.key,.txt,text/plain"
          hidden
          disabled={disabled}
          onChange={onFile}
        />
      </Box>
      <Box
        component="textarea"
        data-hover-scroll
        value={value}
        disabled={disabled}
        onChange={(e) => onChange(e.target.value)}
        placeholder={label}
        sx={{
          flex: 1,
          width: "100%",
          minHeight: 72,
          maxHeight: 100,
          border: "none",
          outline: "none",
          resize: "vertical",
          overflowY: "auto",
          fontSize: 14,
          lineHeight: "18px",
          fontFamily: "inherit",
          color: TEXT_PRIMARY,
          backgroundColor: "transparent",
          p: 0,
          ...hoverScrollbarSx,
        }}
      />
    </Box>
  );
}

/** Install SSL post-action status (Upload ready, etc.) — TipBanner format (info icon + light blue). */
function InstallPanelStatusMessage({ children }) {
  const text = React.Children.toArray(children)
    .map((c) => (typeof c === "string" || typeof c === "number" ? String(c) : ""))
    .join(" ")
    .replace(/\s+/g, " ")
    .trim();
  return (
    <Box role="status" sx={{ width: "100%", alignSelf: "stretch" }}>
      <TipBanner text={text} />
    </Box>
  );
}

/**
 * Domain verified success panel (title + body, check icon) — design ref for Universal verify complete.
 */
function DomainVerifiedBanner({
  title = "Domain verified successfully",
  description = "Universal certificate is active. Continue to WAF when ready.",
}) {
  return (
    <Box
      role="status"
      sx={{
        display: "flex",
        alignItems: "flex-start",
        gap: "12px",
        width: "100%",
        minWidth: 0,
        maxWidth: "100%",
        px: "12px",
        py: "10px",
        backgroundColor: colors.primary[10],
        border: `1px solid ${colors.primary[200]}`,
        borderRadius: "5px",
        boxSizing: "border-box",
      }}
    >
      <CheckCircleIcon
        sx={{ width: 20, height: 20, color: colors.primary[300], flexShrink: 0, mt: "1px" }}
        aria-hidden
      />
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography
          sx={{
            fontSize: 13,
            lineHeight: "18px",
            fontWeight: 600,
            color: colors.primary[400],
            overflowWrap: "anywhere",
          }}
        >
          {title}
        </Typography>
        <Typography
          sx={{
            fontSize: 12,
            lineHeight: "18px",
            fontWeight: 400,
            color: colors.primary[300],
            mt: "2px",
            overflowWrap: "anywhere",
          }}
        >
          {description}
        </Typography>
      </Box>
    </Box>
  );
}

function InstallSslRightPanel({
  certType,
  uploadFullchain,
  setUploadFullchain,
  uploadPrivkey,
  setUploadPrivkey,
  cnameValidations,
  onVerifyDomain,
  verifyLoading,
  sslCertificateReady,
  originCert,
  onGenerateOrigin,
  originLoading,
  originCertificateReady = false,
  /** True only after successful Verify Domain (or server already has a cert host). */
  universalDomainVerified = false,
  /** Classic SSLConfigurationUpload / SSLConfigurationGenerateOrigin — Save/Generate hidden for org users. */
  mutateDisabled = false,
  /** Classic SSLConfigurationFree — Universal has no org gate; Retry/Verify stay enabled for org users. */
  universalActionsDisabled = false,
  freeCertError = null,
  onRetryGenerate = null,
  /** Reason Continue is blocked, surfaced inline in this panel instead of the footer. */
  continueHint = null,
}) {
  const ContinueHint = continueHint ? (
    <Typography
      id="ad2-install-continue-blocked"
      sx={{ fontSize: 12, lineHeight: "18px", color: colors.neutral[700], mt: "auto", pt: "4px" }}
    >
      {continueHint}
    </Typography>
  ) : null;

  const readFile = (event, setter) => {
    const file = event.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => setter(String(reader.result || ""));
    reader.readAsText(file);
    event.target.value = "";
  };

  if (certType === "upload") {
    const pemsReady = !getUploadPemBlockReason(uploadFullchain, uploadPrivkey);
    return (
      <Box
        sx={{
          width: "100%",
          height: "100%",
          minWidth: 0,
          border: `1px solid ${FIELD_BORDER}`,
          borderRadius: "12px",
          p: "12px",
          boxSizing: "border-box",
          backgroundColor: colors.neutral[100],
          display: "flex",
          flexDirection: "column",
          gap: "8px",
          ...revealChildScrollbarsOnHoverSx,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 1, flexShrink: 0 }}>
          <Box>
            <Typography sx={{ fontSize: 16, lineHeight: "18px", fontWeight: 500, color: TEXT_PRIMARY }}>
              Upload SSL certificate
            </Typography>
            <Typography sx={{ fontSize: 12, lineHeight: "18px", fontWeight: 400, color: colors.neutral[700], mt: "4px" }}>
              Secure your domain by uploading a valid SSL/TLS certificate. Paste both PEMs, then continue to WAF to
              install them.
            </Typography>
          </Box>
          {sslCertificateReady && <WafStatusBadge enabled />}
        </Box>
        <UploadPemField
          label="Upload your own fullchain"
          value={uploadFullchain}
          onChange={setUploadFullchain}
          onFile={(e) => readFile(e, setUploadFullchain)}
          disabled={mutateDisabled}
        />
        <UploadPemField
          label="Upload your own privkey"
          value={uploadPrivkey}
          onChange={setUploadPrivkey}
          onFile={(e) => readFile(e, setUploadPrivkey)}
          disabled={mutateDisabled}
        />
        {pemsReady && !mutateDisabled ? (
          <InstallPanelStatusMessage>
            Certificate files are ready. Click Continue to WAF to install them on this site.
          </InstallPanelStatusMessage>
        ) : sslCertificateReady ? (
          <InstallPanelStatusMessage>
            A certificate is already installed for this site. Replace the PEMs above and continue if you need to update
            it.
          </InstallPanelStatusMessage>
        ) : null}
        {ContinueHint}
      </Box>
    );
  }

  if (certType === "generate") {
    const hasOriginCert = Boolean(originCert && (originCert.fullchain || originCert.privkey));
    const originReady = hasOriginCert || originCertificateReady;
    return (
      <Box
        sx={{
          width: "100%",
          height: "100%",
          minWidth: 0,
          overflow: "hidden",
          border: `1px solid ${FIELD_BORDER}`,
          borderRadius: "12px",
          p: "12px",
          boxSizing: "border-box",
          backgroundColor: colors.neutral[100],
          display: "flex",
          flexDirection: "column",
          gap: "12px",
          ...revealChildScrollbarsOnHoverSx,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 1, flexShrink: 0 }}>
          <Box>
            <Typography sx={{ fontSize: 16, lineHeight: "18px", fontWeight: 500, color: TEXT_PRIMARY }}>
              Generate Origin certificate
            </Typography>
            <Typography sx={{ fontSize: 12, lineHeight: "18px", fontWeight: 400, color: colors.neutral[700], mt: "4px" }}>
              Generate a certificate to secure connections between your server and Layerd AI.
            </Typography>
          </Box>
          {originCertificateReady && <WafStatusBadge enabled />}
        </Box>
        <CopyableField
          label="Origin certificate (fullchain)"
          value={originCert?.fullchain}
          multiline
          minRows={2}
          compact
        />
        <CopyableField label="Origin private key" value={originCert?.privkey} multiline minRows={2} compact />
        {originReady ? (
          !mutateDisabled ? (
            <Button
              onClick={onGenerateOrigin}
              disabled={originLoading}
              endIcon={originLoading ? <CircularProgress size={16} sx={{ color: colors.primary[300] }} /> : undefined}
              sx={{ ...outlinedButtonSx, height: 36, alignSelf: "flex-start" }}
            >
              {originLoading ? "Regenerating..." : "Regenerate"}
            </Button>
          ) : null
        ) : (
          <Button
            onClick={onGenerateOrigin}
            disabled={originLoading || mutateDisabled}
            endIcon={originLoading ? <CircularProgress size={16} sx={{ color: "#fff" }} /> : undefined}
            sx={{ ...containedButtonSx, height: 36, alignSelf: "flex-start", flexShrink: 0 }}
          >
            {originLoading ? "Generating..." : "Generate"}
          </Button>
        )}
        {ContinueHint}
      </Box>
    );
  }

  // Universal (free) — auto-generate fills Name/Value, then Verify; after success, status message replaces the button.
  const hasCname = hasCnameValidationRecords(cnameValidations);
  if (verifyLoading && !hasCname && !freeCertError) {
    return (
      <Box
        sx={{
          width: "100%",
          height: "100%",
          border: `1px solid ${FIELD_BORDER}`,
          borderRadius: "12px",
          p: "12px",
          boxSizing: "border-box",
          backgroundColor: colors.neutral[100],
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          minHeight: 200,
        }}
      >
        <CircularProgress size={28} />
      </Box>
    );
  }

  return (
    <Box
      sx={{
        width: "100%",
        height: "100%",
        minWidth: 0,
        overflow: "hidden",
        border: `1px solid ${FIELD_BORDER}`,
        borderRadius: "12px",
        p: "12px",
        boxSizing: "border-box",
        backgroundColor: colors.neutral[100],
        display: "flex",
        flexDirection: "column",
        gap: "12px",
        ...revealChildScrollbarsOnHoverSx,
      }}
    >
      <Box sx={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 1 }}>
        <Box>
          <Typography sx={{ fontSize: 16, lineHeight: "18px", fontWeight: 500, color: TEXT_PRIMARY }}>
            Universal
          </Typography>
          <Typography sx={{ fontSize: 12, lineHeight: "18px", fontWeight: 400, color: colors.neutral[700], mt: "4px" }}>
            {universalDomainVerified
              ? "Domain verification is complete. You can continue to the next step."
              : "Please add the following CNAME record on your DNS manager and click Verify Domain."}
          </Typography>
        </Box>
        {(universalDomainVerified || sslCertificateReady) && <WafStatusBadge enabled />}
      </Box>
      <CopyableField label="Type" value="CNAME" compact />
      <CopyableField label="Name" value={hasCname ? cnameValidations[0] : ""} compact />
      <CopyableField
        label="Value"
        value={hasCname ? cnameValidations[1] : ""}
        multiline
        minRows={2}
        compact
      />
      {freeCertError && (
        <Stack spacing="8px" sx={{ width: "100%" }}>
          <Typography sx={{ fontSize: 12, lineHeight: "18px", color: "#C62828" }}>
            {freeCertError}
          </Typography>
          <Button
            onClick={onRetryGenerate}
            disabled={verifyLoading || universalActionsDisabled || !onRetryGenerate}
            endIcon={verifyLoading ? <CircularProgress size={14} /> : undefined}
            sx={{ ...outlinedButtonSx, height: 36, alignSelf: "flex-start" }}
          >
            {verifyLoading ? "Retrying..." : "Retry"}
          </Button>
        </Stack>
      )}
      {universalDomainVerified ? (
        <DomainVerifiedBanner />
      ) : (
        <Button
          onClick={onVerifyDomain}
          disabled={verifyLoading || universalActionsDisabled || !hasCname}
          endIcon={verifyLoading ? <CircularProgress size={16} sx={{ color: "#fff" }} /> : undefined}
          sx={{ ...containedButtonSx, height: 36, alignSelf: "flex-start" }}
        >
          {verifyLoading ? "Verifying..." : "Verify Domain"}
        </Button>
      )}
      {ContinueHint}
    </Box>
  );
}

const AddDomainWebsiteInformationModal = ({ open, onClose, onCreated, resumeSite = null }) => {
  const { createSite, selectSite, cursite, settingApply, onCreateSuccess, saveSiteWizardStep, getMcpOnboardImpact } = useSite();
  // MCP_ONBOARD_WARN_2026_11_03 — confirm before onboarding a hostname that
  // existing MCP endpoints on other sites claim.
  const [mcpImpact, setMcpImpact] = useState(null);
  const mcpImpactAckRef = useRef(false);
  const {
    sslConfig,
    getSSLConfig,
    sslTypeChange,
    configSslSetting,
    generateCerts,
    verifyDomains,
    getOriginCert,
    setOriginCert: generateOriginCert,
    clearWildcardCerts,
    cert_id,
    cname_validations,
  } = useSSLConfig();
  const { wafConfig, getWAFConfig, configWafSetting, getEdgeConfig } = useWAFConfig();
  const { projectList, curProjectId, getAllProjectBaseList } = useHome();
  const { showError, showSuccess } = useNotification();
  const { userType } = useAuth();
  const { hasPermission, PERMISSIONS } = usePermissions();
  const isOrgUser = userType === UserType.ORG_USER;
  const canConfigureWAF = hasPermission(PERMISSIONS.WAF_CONFIGURE);

  const headerIsConcreteProject = Boolean(curProjectId) && curProjectId !== "all" && curProjectId !== "default";
  const [projectId, setProjectId] = useState(() => (headerIsConcreteProject ? curProjectId : ""));
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [siteID, setSiteID] = useState("");
  const [siteIP, setSiteIP] = useState("");
  const [siteIDError, setSiteIDError] = useState(null);
  const [siteIPError, setSiteIPError] = useState(null);
  const [websiteTouched, setWebsiteTouched] = useState(false);
  const [originTouched, setOriginTouched] = useState(false);
  const [focusedField, setFocusedField] = useState(null);
  const [wizardStep, setWizardStep] = useState(0);
  const [siteCreatedThisSession, setSiteCreatedThisSession] = useState(false);
  /** Site uid returned by selectSite — required by all SSL/WAF/apply API calls. */
  const [siteUid, setSiteUid] = useState(null);
  const [sslType, setSslType] = useState(SslType.OFF);
  /** Install SSL (LE-441): free | upload | generate. */
  const [certType, setCertType] = useState("free");
  const [httpsRedirectEnabled, setHttpsRedirectEnabled] = useState(false);
  const [uploadFullchain, setUploadFullchain] = useState("");
  const [uploadPrivkey, setUploadPrivkey] = useState("");
  const [originCert, setOriginCertState] = useState(null);
  const [originCertLoading, setOriginCertLoading] = useState(false);
  const [freeCertLoading, setFreeCertLoading] = useState(false);
  const [freeCertError, setFreeCertError] = useState(null);
  /** After Universal Verify Domain succeeds (or server already has cert host). */
  const [universalDomainVerified, setUniversalDomainVerified] = useState(false);
  /** WAF (LE-442): toggles synced from wafConfig, persisted via configWafSetting. */
  const [smartParseActive, setSmartParseActive] = useState(true);
  const [signatureWafActive, setSignatureWafActive] = useState(false);
  /** LE-443: classic ConfigurationProcess timer animation (Done / Skipped / Error / In Progress). */
  const [configProcessProgress, setConfigProcessProgress] = useState(0);
  const [configProcessTimerTick, setConfigProcessTimerTick] = useState(0);
  const [configProcessError, setConfigProcessError] = useState(false);
  const configProcessTimerRef = useRef(null);
  /** Furthest wizard page visited this open — Resume stays here even after Back. */
  const furthestWizardStepRef = useRef(0);
  /** Review / Finish (LE-444): live edge DNS + per-step async submit gates. */
  const [edgeConfig, setEdgeConfig] = useState(null);
  const [edgeConfigLoading, setEdgeConfigLoading] = useState(false);
  const [isSslSubmitting, setIsSslSubmitting] = useState(false);
  const [isInstallSubmitting, setIsInstallSubmitting] = useState(false);
  const [isWafSubmitting, setIsWafSubmitting] = useState(false);
  const [isFinishSubmitting, setIsFinishSubmitting] = useState(false);
  /** Outside / X / Escape: confirm discard before closing the Add Domain 2 wizard. */
  const [discardConfirmOpen, setDiscardConfirmOpen] = useState(false);
  /** Resume: hide SSL/WAF panels until live wizard_step + configs are applied (no SSL→WAF flash). */
  const [resumeHydrating, setResumeHydrating] = useState(false);
  /** "forward" = Next (enter from right); "back" = Back (enter from left) */
  const [navDir, setNavDir] = useState("forward");
  const [isStepAnimating, setIsStepAnimating] = useState(false);
  const [transitionPhase, setTransitionPhase] = useState("idle"); // idle | exit | enter | enter-from

  const siteIDRef = useRef(null);
  const siteIPRef = useRef(null);
  const wasOpenRef = useRef(false);
  const stepAnimTimersRef = useRef([]);
  /** Sites where WAF Continue already persisted toggles this session (mirrors classic WAFConfiguration.js). */
  const wafTogglesSavedRef = useRef(new Set());
  /** User touched HTTP→HTTPS switch — do not clobber local choice on sslConfig refresh. */
  const httpsRedirectTouchedRef = useRef(false);

  const showProjectSelect = projectList?.length > 0;
  const effectiveProjectId = showProjectSelect ? projectId : headerIsConcreteProject ? curProjectId : "";
  const fieldsLocked = siteCreatedThisSession;

  useEffect(() => {
    if (!open) {
      wasOpenRef.current = false;
      return;
    }
    const justOpened = !wasOpenRef.current;
    wasOpenRef.current = true;
    if (!justOpened) return;

    const resetChrome = () => {
      setSiteIDError(null);
      setSiteIPError(null);
      setWebsiteTouched(false);
      setOriginTouched(false);
      setFocusedField(null);
      setIsSubmitting(false);
      setUploadFullchain("");
      setUploadPrivkey("");
      setOriginCertState(null);
      setOriginCertLoading(false);
      setFreeCertLoading(false);
      setFreeCertError(null);
      setConfigProcessProgress(0);
      setConfigProcessTimerTick(0);
      setConfigProcessError(false);
      if (configProcessTimerRef.current) {
        clearInterval(configProcessTimerRef.current);
        configProcessTimerRef.current = null;
      }
      setEdgeConfig(null);
      setEdgeConfigLoading(false);
      setIsSslSubmitting(false);
      setIsInstallSubmitting(false);
      setIsWafSubmitting(false);
      setIsFinishSubmitting(false);
      setDiscardConfirmOpen(false);
      setResumeHydrating(false);
      setNavDir("forward");
      setIsStepAnimating(false);
      setTransitionPhase("idle");
      stepAnimTimersRef.current.forEach((id) => window.clearTimeout(id));
      stepAnimTimersRef.current = [];
    };

    resetChrome();
    furthestWizardStepRef.current = 0;
    httpsRedirectTouchedRef.current = false;

    if (resumeSite?.id) {
      const domain = String(resumeSite.site_id || "").trim();
      const origin = String(resumeSite.addr || "").trim();
      setSiteID(domain);
      setSiteIP(origin);
      setProjectId(resumeSite.project_id || "");
      // Do not paint a list step yet — cache can lag Next/Back; wait for hydrate (spinner only).
      setResumeHydrating(true);
      setSiteCreatedThisSession(true);
      setSiteUid(resumeSite.id);
      // Resume: do not paint LE-389 "Smart Parse ON" defaults — wait for server WAF/SSL.
      setSslType(SslType.OFF);
      setCertType("free");
      setHttpsRedirectEnabled(false);
      setUniversalDomainVerified(false);
      setSmartParseActive(false);
      setSignatureWafActive(false);
      wafTogglesSavedRef.current = new Set([resumeSite.id]);
      clearWildcardCerts();
      let cancelled = false;
      (async () => {
        setIsSubmitting(true);
        try {
          if (!domain) {
            showError("Site is not ready yet");
            return;
          }
          const selected = await selectSite(domain);
          if (cancelled) return;
          if (!selected?.id) {
            showError(`Site ${domain} not found`);
            return;
          }
          const uid = selected.id;
          setSiteUid(uid);
          wafTogglesSavedRef.current = new Set([uid]);
          const sslRes = await getSSLConfig(uid, true);
          if (cancelled) return;
          const wafPayload = await getWAFConfig(uid, true);
          if (cancelled) return;
          const sslPayload = sslRes?.status === "success" ? sslRes.data : null;
          if (sslPayload && !Array.isArray(sslPayload)) {
            if (typeof sslPayload.ssl_type === "number") setSslType(sslPayload.ssl_type);
            setHttpsRedirectEnabled(sslPayload?.https_redirect_enabled === true);
            const inferredCertType =
              sslPayload?.certs?.type === CertificateType.SENSE_GUARD
                ? "free"
                : sslPayload?.certs?.type === CertificateType.CUSTOM
                  ? sslPayload?.sg_certs?.host
                    ? "generate"
                    : "upload"
                  : null;
            if (inferredCertType) setCertType(inferredCertType);
            if (
              sslPayload?.certs?.host?.length &&
              sslPayload?.certs?.type === CertificateType.SENSE_GUARD
            ) {
              setUniversalDomainVerified(true);
            }
          }
          if (wafPayload && !Array.isArray(wafPayload)) {
            setSmartParseActive(wafPayload.smart_parse_module_active === true);
            setSignatureWafActive(wafPayload.sd_sig_module_active === true);
          }
          // Live GET first, then list; never erase list-saved step with SSL inference.
          const resumeStep = resolveResumeWizardStepAfterHydrate(
            selected.wizard_step,
            resumeSite.wizard_step ?? resumeSite.setup_step,
            sslPayload,
            sslPayload?.ssl_type
          );
          furthestWizardStepRef.current = Math.max(furthestWizardStepRef.current, resumeStep);
          setWizardStep(resumeStep);
        } catch (e) {
          if (!cancelled) {
            showError(e?.message || "Failed to resume site setup");
          }
        } finally {
          if (!cancelled) {
            setIsSubmitting(false);
            setResumeHydrating(false);
          }
        }
      })();
      return () => {
        cancelled = true;
      };
    }

    setSiteID("");
    setSiteIP("");
    setProjectId(headerIsConcreteProject ? curProjectId : "");
    setWizardStep(0);
    furthestWizardStepRef.current = 0;
    setResumeHydrating(false);
    setSiteCreatedThisSession(false);
    setSiteUid(null);
    setSslType(SslType.OFF);
    setCertType("free");
    setHttpsRedirectEnabled(false);
    setUniversalDomainVerified(false);
    setSmartParseActive(true);
    setSignatureWafActive(false);
    wafTogglesSavedRef.current = new Set();
    clearWildcardCerts();
  }, [
    open,
    resumeSite,
    curProjectId,
    headerIsConcreteProject,
    clearWildcardCerts,
    selectSite,
    getSSLConfig,
    getWAFConfig,
    showError,
  ]);

  useEffect(() => {
    return () => {
      stepAnimTimersRef.current.forEach((id) => window.clearTimeout(id));
      stepAnimTimersRef.current = [];
    };
  }, []);

  useEffect(() => {
    if (projectList !== null) return;
    getAllProjectBaseList();
  }, [projectList, getAllProjectBaseList]);

  /**
   * Sync SSL UI from server while the user is still on SSL Configuration (step 1).
   * From Install SSL onward, local mode/redirect edits must not be clobbered by refreshes.
   */
  useEffect(() => {
    if (!sslConfig || Array.isArray(sslConfig)) return;
    if (wizardStep >= 2) return;
    if (typeof sslConfig.ssl_type === "number") {
      setSslType(sslConfig.ssl_type);
    }
    if (!httpsRedirectTouchedRef.current) {
      setHttpsRedirectEnabled(sslConfig?.https_redirect_enabled === true);
    }
    const inferredCertType =
      sslConfig?.certs?.type === CertificateType.SENSE_GUARD
        ? "free"
        : sslConfig?.certs?.type === CertificateType.CUSTOM
          ? sslConfig?.sg_certs?.host
            ? "generate"
            : "upload"
          : null;
    if (inferredCertType) {
      setCertType(inferredCertType);
    }
    // Universal installed when host exists and type is Sense Guard (not plain custom upload).
    if (
      sslConfig?.certs?.host?.length &&
      sslConfig?.certs?.type === CertificateType.SENSE_GUARD
    ) {
      setUniversalDomainVerified(true);
    }
  }, [sslConfig, wizardStep]);

  /** Sync WAF toggles from wafConfig on load (LE-389: missing smart_parse → ON).
   * Resume skips the force-ON effect via wafTogglesSavedRef and applies fetch result first. */
  useEffect(() => {
    if (!wafConfig || Array.isArray(wafConfig)) return;
    setSmartParseActive(wafConfig.smart_parse_module_active ?? true);
    setSignatureWafActive(wafConfig.sd_sig_module_active ?? false);
  }, [wafConfig]);

  /** New Add Domain only: until first WAF save, force Smart Parse ON if server still has false (LE-389). */
  useEffect(() => {
    if (!wafConfig || Array.isArray(wafConfig) || !siteUid) return;
    if (wafTogglesSavedRef.current.has(siteUid)) return;
    if (wafConfig.smart_parse_module_active === false) {
      setSmartParseActive(true);
    }
  }, [wafConfig, siteUid]);

  /** Universal (free): auto generateCerts like SSLConfigurationFree when Install opens. */
  useEffect(() => {
    // Classic SSLConfigurationFree has no org gate — Universal is self-service for everyone.
    if (!open || wizardStep !== 2 || certType !== "free" || !siteUid) return;
    if (hasCnameValidationRecords(cname_validations)) {
      setFreeCertError(null);
      return;
    }
    let cancelled = false;
    (async () => {
      setFreeCertLoading(true);
      setFreeCertError(null);
      try {
        await generateCerts(siteUid);
      } catch (e) {
        if (!cancelled) {
          const message = e?.message || "Failed to generate certificate";
          setFreeCertError(message);
        }
      } finally {
        if (!cancelled) setFreeCertLoading(false);
      }
    })();
    return () => {
      cancelled = true;
      setFreeCertLoading(false);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, wizardStep, certType, siteUid, cname_validations]);

  const handleRetryFreeGenerate = async () => {
    if (!siteUid || freeCertLoading) return;
    setFreeCertLoading(true);
    try {
      await generateCerts(siteUid);
      setFreeCertError(null);
    } catch (e) {
      const message = e?.message || "Failed to generate certificate";
      setFreeCertError(message);
    } finally {
      setFreeCertLoading(false);
    }
  };
  /** Generate Origin cert: fetch existing sg_certs once Install SSL opens on this method. */
  useEffect(() => {
    if (!open || wizardStep !== 2 || certType !== "generate" || !siteUid) return;
    let cancelled = false;
    (async () => {
      setOriginCertLoading(true);
      try {
        const cert = await getOriginCert(siteUid);
        if (!cancelled) setOriginCertState(cert || null);
      } catch (e) {
        if (!cancelled) showError(e?.message || "Failed to load origin certificate");
      } finally {
        if (!cancelled) setOriginCertLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, wizardStep, certType, siteUid]);

  /** Configuration Process: fetch missing config + classic 500ms pass/fail animation. */
  useEffect(() => {
    if (!open || wizardStep !== 4 || !siteUid) return;
    if (!sslConfig || Array.isArray(sslConfig)) getSSLConfig(siteUid);
    if (!wafConfig || Array.isArray(wafConfig)) getWAFConfig(siteUid);
  }, [open, wizardStep, siteUid, sslConfig, wafConfig, getSSLConfig, getWAFConfig]);

  const configProcessFetchFailed = isConfigProcessFetchFailed(sslConfig, wafConfig, siteUid);
  const sslConfigReady = sslConfig && !Array.isArray(sslConfig);
  const wafConfigReady = wafConfig && !Array.isArray(wafConfig);

  /** Reset animation when Config Process step opens (classic starts from progress 0). */
  useEffect(() => {
    if (!open || wizardStep !== 4) {
      if (configProcessTimerRef.current) {
        clearInterval(configProcessTimerRef.current);
        configProcessTimerRef.current = null;
      }
      return;
    }
    setConfigProcessProgress(0);
    setConfigProcessTimerTick(0);
    setConfigProcessError(false);
    if (configProcessTimerRef.current) {
      clearInterval(configProcessTimerRef.current);
      configProcessTimerRef.current = null;
    }
  }, [open, wizardStep]);

  const updateConfigProcessProgress = useCallback(() => {
    const oldProgress = configProcessProgress;
    let newProgress = 0;
    setConfigProcessTimerTick((oldTick) => oldTick + 1);
    switch (oldProgress) {
      case 0:
        newProgress = 1;
        break;
      case 1:
        if (
          SslType.OFF === sslConfig?.ssl_type ||
          CertificateType.SENSE_GUARD === sslConfig?.certs?.type ||
          sslConfig?.certs?.host?.length
        ) {
          newProgress = 2;
        } else if (
          SslType.OFF < sslConfig?.ssl_type &&
          CertificateType.CUSTOM === sslConfig?.certs?.type &&
          !sslConfig?.certs?.host?.length
        ) {
          newProgress = 2;
          if (configProcessTimerRef.current) {
            clearInterval(configProcessTimerRef.current);
            configProcessTimerRef.current = null;
          }
          setConfigProcessError(true);
        } else {
          if (0 === configProcessTimerTick % 10) {
            getSSLConfig(siteUid);
          }
          newProgress = 1;
        }
        break;
      case 2:
        if (SslType.OFF === sslConfig?.ssl_type || sslConfig?.certs?.host?.length) {
          newProgress = 3;
        } else {
          if (0 === configProcessTimerTick % 10) {
            getSSLConfig(siteUid);
          }
          newProgress = 2;
        }
        break;
      case 3:
        newProgress = 4;
        break;
      case 4:
        newProgress = 5;
        break;
      case 5:
        if (configProcessTimerRef.current) {
          clearInterval(configProcessTimerRef.current);
          configProcessTimerRef.current = null;
        }
        newProgress = 6;
        break;
      default:
        newProgress = 6;
        break;
    }
    setConfigProcessProgress(newProgress);
  }, [configProcessProgress, sslConfig, siteUid, getSSLConfig, configProcessTimerTick]);

  useEffect(() => {
    if (!open || wizardStep !== 4) return;
    if (configProcessFetchFailed || configProcessError) return;
    if (configProcessProgress >= CONFIG_PROCESS_STEPS.length) return;
    if (sslConfigReady && wafConfigReady && configProcessTimerRef.current === null) {
      configProcessTimerRef.current = setInterval(() => {
        updateConfigProcessProgress();
      }, 500);
    }
    return () => {
      if (configProcessTimerRef.current) {
        clearInterval(configProcessTimerRef.current);
        configProcessTimerRef.current = null;
      }
    };
  }, [
    open,
    wizardStep,
    sslConfigReady,
    wafConfigReady,
    configProcessFetchFailed,
    configProcessError,
    configProcessProgress,
    updateConfigProcessProgress,
  ]);

  const getConfigProcessStepStatus = (step) => {
    if (step < configProcessProgress) {
      switch (step) {
        case 1:
          return SslType.OFF === sslConfig?.ssl_type
            ? ConfigStepStatus.SKIPPED
            : sslConfig?.certs?.host?.length || CertificateType.SENSE_GUARD === sslConfig?.certs?.type
              ? ConfigStepStatus.DONE
              : ConfigStepStatus.ERROR;
        case 2:
          return SslType.OFF < sslConfig?.ssl_type
            ? sslConfig?.certs?.host?.length
              ? ConfigStepStatus.DONE
              : ConfigStepStatus.ERROR
            : ConfigStepStatus.SKIPPED;
        case 3:
          return SslType.OFF < sslConfig?.ssl_type && true === sslConfig?.https_redirect_enabled
            ? ConfigStepStatus.DONE
            : ConfigStepStatus.SKIPPED;
        case 4:
          return true === wafConfig?.sd_sig_module_active ? ConfigStepStatus.DONE : ConfigStepStatus.SKIPPED;
        default:
          return ConfigStepStatus.DONE;
      }
    } else if (step === configProcessProgress && !configProcessError) {
      return ConfigStepStatus.ACTIVE;
    }
    return ConfigStepStatus.PENDING;
  };

  const configProcessPercent = Math.round((configProcessProgress / CONFIG_PROCESS_STEPS.length) * 100);
  const configProcessAnimComplete = configProcessProgress >= CONFIG_PROCESS_STEPS.length;
  /** Block Continue to Review while deploy animation is still running; allow after complete, error, or fetch fail. */
  const configProcessContinueEnabled =
    configProcessAnimComplete || configProcessError || configProcessFetchFailed;

  const handleConfigProcessRetry = () => {
    if (!siteUid) return;
    setConfigProcessProgress(0);
    setConfigProcessTimerTick(0);
    setConfigProcessError(false);
    if (configProcessTimerRef.current) {
      clearInterval(configProcessTimerRef.current);
      configProcessTimerRef.current = null;
    }
    getSSLConfig(siteUid);
    getWAFConfig(siteUid);
  };

  /** Review: edge DNS via same getEdgeConfig as classic Finalize. */
  useEffect(() => {
    if (!open || wizardStep !== 5) return;
    let cancelled = false;
    setEdgeConfig(null);
    setEdgeConfigLoading(true);
    (async () => {
      try {
        const res = await getEdgeConfig();
        if (!cancelled) setEdgeConfig(res || null);
      } catch (e) {
        /* getEdgeConfig already surfaces the failure via context errMsg */
      } finally {
        if (!cancelled) setEdgeConfigLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [open, wizardStep, getEdgeConfig]);

  const tipText =
    focusedField === "website" ? TIP_WEBSITE : focusedField === "origin" ? TIP_ORIGIN : TIP_IDLE;

  const sslTips = SSL_TIPS_BY_MODE[sslType] || SSL_TIPS_BY_MODE[SslType.OFF];
  const installTips = INSTALL_TIPS_BY_CERT[certType] || INSTALL_TIPS_BY_CERT.free;
  const wafTipPrimary =
    smartParseActive && signatureWafActive
      ? "Both Layerd AI Smart Parse and Signature WAF are enabled for defence in depth."
      : smartParseActive
        ? "Layerd AI Smart Parse is enabled for AI-powered request parsing."
        : signatureWafActive
          ? "Signature WAF is enabled for Layerd Signature Detection."
          : "Both WAF modules are off. Enable one or both according to your security needs.";

  const applyWebsiteValidation = (value) => {
    const check = validateAddDomainWebsite(value);
    setSiteIDError(check.valid ? null : check.message);
    return check.valid;
  };

  const applyOriginValidation = (value) => {
    const check = validateAddDomainOrigin(value);
    setSiteIPError(check.valid ? null : check.message);
    return check.valid;
  };

  const clearStepAnimTimers = () => {
    stepAnimTimersRef.current.forEach((id) => window.clearTimeout(id));
    stepAnimTimersRef.current = [];
  };

  /** Option 1: exit current (dir), swap, enter next (opposite side). Blocks input while animating. */
  const navigateStep = (nextStep) => {
    if (isStepAnimating || isSubmitting) return;
    if (nextStep === wizardStep) return;
    if (nextStep < 0 || nextStep >= WIZARD_STEP_COUNT) return;

    const dir = nextStep > wizardStep ? "forward" : "back";
    // Persist only when advancing past the furthest page visited this session.
    if (
      shouldPersistWizardStepOnNavigate(nextStep, furthestWizardStepRef.current) &&
      siteUid &&
      typeof saveSiteWizardStep === "function"
    ) {
      furthestWizardStepRef.current = nextStep;
      saveSiteWizardStep(siteUid, persistableWizardStep(nextStep)).catch(() => {});
    }
    clearStepAnimTimers();
    setNavDir(dir);
    setIsStepAnimating(true);
    setFocusedField(null);
    setTransitionPhase("exit");

    const exitTimer = window.setTimeout(() => {
      setWizardStep(nextStep);
      setTransitionPhase("enter-from");
      window.requestAnimationFrame(() => {
        window.requestAnimationFrame(() => {
          setTransitionPhase("idle");
          const doneTimer = window.setTimeout(() => {
            setIsStepAnimating(false);
          }, SLIDE_MS);
          stepAnimTimersRef.current.push(doneTimer);
        });
      });
    }, SLIDE_MS);
    stepAnimTimersRef.current.push(exitTimer);
  };

  const goToSslStep = () => {
    navigateStep(1);
  };

  const handleWebsiteNext = async () => {
    if (isStepAnimating) return;
    setWebsiteTouched(true);
    setOriginTouched(true);
    const hostnameOk = applyWebsiteValidation(siteID);
    const originOk = applyOriginValidation(siteIP);
    if (!hostnameOk) {
      siteIDRef.current?.focus();
      return;
    }
    if (!originOk) {
      siteIPRef.current?.focus();
      return;
    }

    if (siteCreatedThisSession) {
      // Create already succeeded earlier; if select never landed siteUid, retry select + SSL bootstrap.
      if (!siteUid) {
        setIsSubmitting(true);
        try {
          const selectedSite = await selectSite(String(siteID).trim());
          if (!selectedSite) {
            showError(`Site ${siteID} not found`);
            return;
          }
          setSiteUid(selectedSite.id);
          if (typeof saveSiteWizardStep === "function") {
            saveSiteWizardStep(selectedSite.id, persistableWizardStep(1)).catch(() => {});
          }
          try {
            await getSSLConfig(selectedSite.id, true);
          } catch (e) {
            showError(e?.message || "Failed to load SSL configuration");
          }
        } catch (e) {
          showError(e?.message || "Failed to load site");
          return;
        } finally {
          setIsSubmitting(false);
        }
      }
      goToSslStep();
      return;
    }

    if (!mcpImpactAckRef.current && typeof getMcpOnboardImpact === "function") {
      setIsSubmitting(true);
      const impact = await getMcpOnboardImpact(String(siteID).trim());
      setIsSubmitting(false);
      if (impact && Array.isArray(impact.endpoints) && impact.endpoints.length > 0) {
        setMcpImpact(impact);
        return;
      }
    }
    mcpImpactAckRef.current = false;

    setIsSubmitting(true);
    try {
      const result = await createSite(String(siteID).trim(), String(siteIP).trim(), effectiveProjectId);
      if (!result || result.status !== "success") {
        showError(result?.msg || "Failed to create site");
        return;
      }

      // Create succeeded — do not retry create; select/SSL may still need a retry via siteCreatedThisSession path.
      setSiteCreatedThisSession(true);

      const selectedSite = await selectSite(String(siteID).trim());
      if (!selectedSite) {
        showError(`Site ${siteID} not found`);
        return;
      }
      setSiteUid(selectedSite.id);
      if (typeof saveSiteWizardStep === "function") {
        saveSiteWizardStep(selectedSite.id, persistableWizardStep(1)).catch(() => {});
      }

      // WI parity: fetch SSL config right after create+select. Classic still advances on failure.
      try {
        await getSSLConfig(selectedSite.id, true);
      } catch (e) {
        showError(e?.message || "Failed to load SSL configuration");
      }

      // Success toast only on Finish (last step) — intermediate steps: errors only.
      try {
        if (onCreated) await onCreated(selectedSite);
      } catch (e) {
        showError(e?.message || "Failed to refresh sites list");
      }
      goToSslStep();
    } catch (e) {
      showError(e?.message || "Failed to create site");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleWizardBack = () => {
    // Config Process cert error tip says Install SSL — jump there when error is active.
    if (wizardStep === 4 && configProcessError) {
      navigateStep(2);
      return;
    }
    // InSecure path skips Install SSL — Back from WAF returns to SSL Configuration.
    if (wizardStep === 3 && sslType === SslType.OFF) {
      navigateStep(1);
      return;
    }
    navigateStep(Math.max(0, wizardStep - 1));
  };

  /**
   * SSL Next: persist mode on Next when it differs from loaded config.
   * InSecure skips Install and loads WAF; encrypted modes go to Install SSL.
   */
  const handleSslNext = async () => {
    if (isStepAnimating || isSslSubmitting) return;
    if (isOrgUser) {
      // Org users cannot mutate SSL mode — advance with current server state only.
      if (sslType === SslType.OFF) {
        if (!siteUid) {
          showError("Site is not ready yet");
          return;
        }
        setIsSslSubmitting(true);
        try {
          await getWAFConfig(siteUid, true);
          navigateStep(3);
        } catch (e) {
          showError(e?.message || "Failed to load WAF configuration");
        } finally {
          setIsSslSubmitting(false);
        }
      } else {
        navigateStep(2);
      }
      return;
    }
    if (!siteUid) {
      showError("Site is not ready yet");
      return;
    }
    setIsSslSubmitting(true);
    try {
      const loadedSslType =
        sslConfig && !Array.isArray(sslConfig) && typeof sslConfig.ssl_type === "number"
          ? sslConfig.ssl_type
          : undefined;
      if (loadedSslType !== sslType) {
        await sslTypeChange(siteUid, sslType);
        const refreshed = await getSSLConfig(siteUid);
        const persistedType =
          refreshed?.status === "success" && refreshed.data && !Array.isArray(refreshed.data)
            ? refreshed.data.ssl_type
            : undefined;
        if (persistedType !== sslType) {
          showError("Failed to update SSL configuration");
          return;
        }
      }
      if (sslType === SslType.OFF || shouldSkipInstallForInsecure(sslType)) {
        await getWAFConfig(siteUid, true);
        navigateStep(3);
      } else {
        navigateStep(2);
      }
    } catch (e) {
      showError(e?.message || "Failed to update SSL configuration");
    } finally {
      setIsSslSubmitting(false);
    }
  };

  /** Universal (free): verify + HTTPS on + OFF→Adaptive bump for non-org users. */
  const handleVerifyFreeDomain = async () => {
    if (!siteUid) {
      showError("Site is not ready yet");
      return;
    }
    if (!hasCnameValidationRecords(cname_validations)) {
      showError("CNAME Name/Value are missing");
      return;
    }
    if (freeCertLoading) return;
    setFreeCertLoading(true);
    try {
      const wasOff = sslType === SslType.OFF;
      await verifyDomains(siteUid, cert_id);
      await getSSLConfig(siteUid);
      await configSslSetting(siteUid, "httpsRedirectChange", { enable: true });
      setHttpsRedirectEnabled(true);
      httpsRedirectTouchedRef.current = true;
      if (wasOff && !isOrgUser) {
        setSslType(SslType.FLEXIBLE);
        await sslTypeChange(siteUid, SslType.FLEXIBLE);
      }
      setFreeCertError(null);
      setUniversalDomainVerified(true);
      // Success toast only on Finish (last step) — Verify shows inline status instead.
    } catch (e) {
      showError(e?.message || "Failed to verify domain");
    } finally {
      setFreeCertLoading(false);
    }
  };

  /** Generate Origin: issue/regenerate sg_certs (SSLConfigurationGenerateOrigin.js parity). */
  const handleGenerateOriginCert = async () => {
    if (!siteUid) {
      showError("Site is not ready yet");
      return;
    }
    if (originCertLoading) return;
    setOriginCertLoading(true);
    try {
      const cert = await generateOriginCert(siteUid);
      setOriginCertState(cert || null);
      try {
        // Refresh sslConfig so FULL_STRICT Continue gates see sg_certs.host / validTo.
        await getSSLConfig(siteUid);
      } catch (e) {
        showError(e?.message || "Origin certificate generated, but SSL refresh failed");
      }
    } catch (e) {
      showError(e?.message || "Failed to generate origin certificate");
    } finally {
      setOriginCertLoading(false);
    }
  };

  /**
   * Install SSL Next: upload persists first, then cert gates, then HTTPS redirect on Continue,
   * then getWAFConfig before advancing to WAF.
   */
  const handleInstallSslNext = async () => {
    if (isStepAnimating || isInstallSubmitting) return;
    if (!siteUid) {
      showError("Site is not ready yet");
      return;
    }
    if (isOrgUser) {
      // Classic: org users cannot mutate certs — only advance when already ready.
      const effectiveSslConfig = sslConfig && !Array.isArray(sslConfig) ? sslConfig : null;
      const block = getInstallContinueBlockReason(effectiveSslConfig, sslType);
      if (block) {
        showError(block);
        return;
      }
      setIsInstallSubmitting(true);
      try {
        await getWAFConfig(siteUid, true);
        navigateStep(3);
      } catch (e) {
        showError(e?.message || "Failed to continue to WAF");
      } finally {
        setIsInstallSubmitting(false);
      }
      return;
    }
    setIsInstallSubmitting(true);
    try {
      // Freshest known config for this call — the `sslConfig` hook value is still the
      // pre-upload snapshot until the next render, so an inline uploadCert must use the
      // response it fetches instead of the stale closure value for the gates below.
      let effectiveSslConfig = sslConfig && !Array.isArray(sslConfig) ? sslConfig : null;
      if (certType === "upload") {
        const pemBlock = getUploadPemBlockReason(uploadFullchain, uploadPrivkey);
        if (pemBlock) {
          showError(pemBlock);
          return;
        }
        const ok = await configSslSetting(siteUid, "uploadCert", {
          fullchain: uploadFullchain,
          privkey: uploadPrivkey,
        });
        if (!ok) {
          showError("Please upload or generate SSL certificates");
          return;
        }
        const refreshed = await getSSLConfig(siteUid);
        if (refreshed?.status === "success") {
          effectiveSslConfig = refreshed.data;
        }
      }

      const block = getInstallContinueBlockReason(effectiveSslConfig, sslType);
      if (block) {
        showError(block);
        return;
      }

      const siteIdForRedirect = cursite?.site_id || String(siteID).trim();
      const baselineRedirect = effectiveSslConfig?.https_redirect_enabled === true;
      if (siteIdForRedirect && shouldPersistHttpsRedirect(baselineRedirect, httpsRedirectEnabled)) {
        await configSslSetting(siteUid, "httpsRedirectChange", {
          site_id: siteIdForRedirect,
          enable: !!httpsRedirectEnabled,
        });
      }

      await getWAFConfig(siteUid, true);
      navigateStep(3);
    } catch (e) {
      showError(e?.message || "Failed to continue to WAF");
    } finally {
      setIsInstallSubmitting(false);
    }
  };

  /** WAF Next: same bulkWafToggleChange API as classic, then Config Process. */
  const handleWafNext = async () => {
    if (isStepAnimating || isWafSubmitting) return;
    if (!siteUid) {
      showError("Site is not ready yet");
      return;
    }
    setIsWafSubmitting(true);
    try {
      // Classic always persists on Continue regardless of WAF_CONFIGURE — switches are
      // read-only when unauthorized, but the write still runs with whatever values are shown.
      await configWafSetting(siteUid, "bulkWafToggleChange", {
        smart_parse_module_active: smartParseActive,
        sd_sig_module_active: signatureWafActive,
      });
      wafTogglesSavedRef.current.add(siteUid);
      setConfigProcessProgress(0);
      setConfigProcessTimerTick(0);
      setConfigProcessError(false);
      if (configProcessTimerRef.current) {
        clearInterval(configProcessTimerRef.current);
        configProcessTimerRef.current = null;
      }
      navigateStep(4);
    } catch (e) {
      showError(e?.message || "Failed to save WAF configuration");
    } finally {
      setIsWafSubmitting(false);
    }
  };

  /** Continue to Review only after config process finishes (or lands on a terminal error/fetch-fail state). */
  const handleConfigProcessNext = () => {
    if (isStepAnimating || !configProcessContinueEnabled) return;
    navigateStep(5);
  };

  /** Same Finish path as classic Finalize: settingApply → onCreateSuccess → refresh → close. */
  const handleReviewFinish = async () => {
    if (isStepAnimating || isFinishSubmitting) return;
    if (!cursite?.site_id) {
      showError("Site is not ready yet");
      return;
    }
    setIsFinishSubmitting(true);
    try {
      const result = await settingApply(cursite.site_id);
      if (result?.status !== "success") {
        showError(result?.msg || "Failed to apply configuration");
        return;
      }
      try {
        await onCreateSuccess(cursite.site_id);
      } catch (e) {
        showError(e?.message || "Configuration applied, but post-create refresh failed");
      }
      // List refresh (including project→site map) is owned by onCreated/handleAddDomain2Created.
      // Avoid clearSites() here — wiping site_List to null without an immediate parent refresh
      // left the table empty until a full page reload in some paths.
      try {
        if (onCreated) await onCreated(cursite, { selectAsCurrent: true });
      } catch (e) {
        showError(e?.message || "Failed to refresh sites list");
      }
      showSuccess(
        resumeSite
          ? `Site ${String(siteID).trim() || "domain"} configured`
          : `Site ${String(siteID).trim() || "domain"} created and configured`
      );
      if (onClose) onClose();
    } catch (e) {
      showError(e?.message || "Failed to finish setup");
    } finally {
      setIsFinishSubmitting(false);
    }
  };

  const exitX = navDir === "forward" ? -SLIDE_PX : SLIDE_PX;
  const enterX = navDir === "forward" ? SLIDE_PX : -SLIDE_PX;
  const stepMotionSx = {
    width: "100%",
    boxSizing: "border-box",
    willChange: "transform, opacity",
    ...(transitionPhase === "exit" && {
      opacity: 0,
      transform: `translateX(${exitX}px)`,
      transition: `opacity ${SLIDE_MS}ms ${SLIDE_EASE}, transform ${SLIDE_MS}ms ${SLIDE_EASE}`,
      pointerEvents: "none",
    }),
    ...(transitionPhase === "enter-from" && {
      opacity: 0,
      transform: `translateX(${enterX}px)`,
      transition: "none",
      pointerEvents: "none",
    }),
    ...(transitionPhase === "idle" && {
      opacity: 1,
      transform: "translateX(0)",
      transition: isStepAnimating
        ? `opacity ${SLIDE_MS}ms ${SLIDE_EASE}, transform ${SLIDE_MS}ms ${SLIDE_EASE}`
        : "none",
      pointerEvents: isStepAnimating ? "none" : "auto",
    }),
  };

  const stepTitle =
    wizardStep === 0
      ? "Website Information"
      : wizardStep === 1
        ? "SSL Configuration"
        : wizardStep === 2
          ? "Install SSL Configuration"
          : wizardStep === 3
            ? "WAF Configuration"
            : wizardStep === 4
              ? "Configuration Process"
              : "Review Configuration";

  const installSslCertificateReady = isSslCertificateReady(sslConfig);
  const effectiveSslConfigForInstall =
    sslConfig && !Array.isArray(sslConfig) ? sslConfig : null;
  const installOriginCertificateReady = isOriginCertificateReady(effectiveSslConfigForInstall);
  const installContinueBlockReason = getInstallContinueBlockReason(
    effectiveSslConfigForInstall,
    sslType
  );
  // AD2 persists upload on Continue (classic uploads in-drawer first). Allow click when PEMs are ready.
  const uploadReadyToSubmit =
    certType === "upload" && !getUploadPemBlockReason(uploadFullchain, uploadPrivkey);
  const installContinueDisabled =
    Boolean(installContinueBlockReason) && !uploadReadyToSubmit;
  const installContinueHint =
    certType === "upload" && getUploadPemBlockReason(uploadFullchain, uploadPrivkey)
      ? getUploadPemBlockReason(uploadFullchain, uploadPrivkey)
      : installContinueDisabled
        ? installContinueBlockReason
        : null;

  // Classic ReviewConfiguration derives Certificate Type from certs.type (not local radio).
  const certTypeLabelForReview =
    sslConfig?.certs?.type === CertificateType.SENSE_GUARD
      ? "Layerd AI managed"
      : sslConfig?.certs?.type === CertificateType.CUSTOM
        ? "User managed"
        : "—";
  // Classic ReviewConfiguration: SSL Status reflects server ssl_type, not local radio.
  const sslEnabledForReview = (sslConfig?.ssl_type ?? sslType) !== SslType.OFF;
  const anyStepSubmitting =
    isSubmitting || isSslSubmitting || isInstallSubmitting || isWafSubmitting || isFinishSubmitting;

  /** Primary CTA copy = next destination / action for the current step (and SSL mode). */
  const primaryCtaLabel =
    wizardStep === 0
      ? isSubmitting
        ? "Creating..."
        : "Continue to SSL"
      : wizardStep === 1
        ? isSslSubmitting
          ? "Saving..."
          : sslType === SslType.OFF
            ? "Continue to WAF"
            : "Continue to Install SSL"
        : wizardStep === 2
          ? isInstallSubmitting
            ? "Saving..."
            : "Continue to WAF"
          : wizardStep === 3
            ? isWafSubmitting
              ? "Saving..."
              : "Continue to Process"
            : wizardStep === 4
              ? "Continue to Review"
              : isFinishSubmitting
                ? "Finishing..."
                : "Finish";

  /**
   * Backdrop / Escape / X: confirm first.
   * Pop 1 (pre-create): discard draft — only before site is created this session.
   * Pop 2 (post-create): site already added — never use selector "all" as the domain label.
   */
  const leaveWizardAfterSiteAdded = Boolean(siteCreatedThisSession || siteUid);
  const leaveWizardDomainRaw = String(siteID || "").trim();
  const leaveWizardDomainLabel =
    leaveWizardDomainRaw && leaveWizardDomainRaw.toLowerCase() !== "all"
      ? leaveWizardDomainRaw
      : "This website";

  const requestWizardClose = () => {
    if (anyStepSubmitting) return;
    setDiscardConfirmOpen(true);
  };

  const handleDiscardCancel = () => {
    setDiscardConfirmOpen(false);
  };

  const handleDiscardConfirm = async () => {
    setDiscardConfirmOpen(false);
    const savedWizardStep =
      String(cursite?.id ?? "") === String(siteUid ?? "")
        ? cursite?.wizard_step ?? cursite?.setup_step
        : resumeSite?.wizard_step ?? resumeSite?.setup_step;
    const persistIndex = furthestWizardStepRef.current;
    if (
      siteUid &&
      typeof saveSiteWizardStep === "function" &&
      shouldPersistWizardStepOnClose(persistIndex, savedWizardStep)
    ) {
      try {
        await saveSiteWizardStep(siteUid, persistableWizardStep(persistIndex));
      } catch (e) {
        showError(e?.message || "Failed to save setup progress");
      }
    }
    if (onClose) onClose();
  };

  const leaveConfirmTitle = leaveWizardAfterSiteAdded
    ? "Leave setup incomplete?"
    : "Leave without adding a domain?";
  const leaveConfirmBody = leaveWizardAfterSiteAdded
    ? `${leaveWizardDomainLabel} has already been added. Closing this window will not delete it. You can complete SSL, WAF, and other settings later using Resume setup on the Sites page.`
    : "You have not finished adding this domain. If you leave now, nothing will be saved and you will need to start over.";
  const leaveConfirmCancelLabel = leaveWizardAfterSiteAdded ? "Keep editing" : "Cancel";
  const leaveConfirmContinueLabel = leaveWizardAfterSiteAdded ? "Close setup" : "Leave";

  return (
    <>
    <Modal
      open={open}
      onClose={anyStepSubmitting ? undefined : requestWizardClose}
      closeAfterTransition
      sx={{ display: "flex", alignItems: "center", justifyContent: "center", p: 2 }}
    >
      <Fade in={open} timeout={280}>
        <Box
          sx={{
            outline: "none",
            width: "100%",
            maxWidth: "min(920px, calc(100vw - 32px))",
            boxSizing: "border-box",
            minHeight: `min(90vh, ${STEP_BODY_MIN_HEIGHT + MODAL_CHROME_PX}px)`,
            height: `min(90vh, ${STEP_BODY_MIN_HEIGHT + MODAL_CHROME_PX}px)`,
            maxHeight: "90vh",
            overflow: "hidden",
            backgroundColor: colors.neutral[100],
            border: `1px solid ${FIELD_BORDER}`,
            borderRadius: "12px",
            p: "24px",
            display: "flex",
            flexDirection: "column",
            justifyContent: "space-between",
            gap: 0,
            boxShadow: "0 16px 48px rgba(17, 17, 17, 0.16)",
            transform: open ? "scale(1)" : "scale(0.96)",
            transition: "transform 280ms ease",
            mx: "auto",
          }}
        >
          <Stack spacing="20px" sx={{ width: "100%", flex: 1, minHeight: 0, overflow: "hidden" }}>
            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", width: "100%" }}>
              <Box sx={{ display: "flex", alignItems: "center", gap: "12px" }}>
                <Box
                  sx={{
                    width: 56,
                    height: 56,
                    borderRadius: "12px",
                    backgroundColor: colors.primary[100],
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    flexShrink: 0,
                  }}
                >
                  <LayerdIcon icon={UI_ICONS.manageDomain} size={28} color="primary" />
                </Box>
                <Box>
                  <Typography
                    sx={{
                      fontSize: 24,
                      lineHeight: "32px",
                      fontWeight: 600,
                      color: TEXT_PRIMARY,
                      textTransform: "capitalize",
                    }}
                  >
                    {stepTitle}
                  </Typography>
                  <Typography
                    sx={{
                      fontSize: 16,
                      lineHeight: "18px",
                      fontWeight: 500,
                      color: colors.primary[300],
                      mt: "2px",
                    }}
                  >
                    Add Domain
                  </Typography>
                </Box>
              </Box>
              <IconButton
                aria-label="Close"
                onClick={requestWizardClose}
                disabled={anyStepSubmitting}
                size="small"
                sx={{ color: colors.neutral[700], p: 0.5 }}
              >
                <CloseIcon sx={{ fontSize: 16 }} />
              </IconButton>
            </Box>

            <ProgressBar activeStep={resumeHydrating ? -1 : wizardStep} />

            <Box
              data-hover-scroll
              sx={{
                width: "100%",
                flex: 1,
                minHeight: 0,
                overflowX: "hidden",
                overflowY: "auto",
                ...hoverScrollbarSx,
              }}
            >
              <Box sx={{ width: "100%", overflowX: "hidden", overflowY: "visible", pb: "4px" }}>
              <Box sx={stepMotionSx}>
                <Box sx={{ width: "100%" }}>
                {resumeHydrating ? (
                  <Stack alignItems="center" justifyContent="center" sx={{ width: "100%", py: 10 }}>
                    <CircularProgress size={32} sx={{ color: colors.primary[400] }} />
                  </Stack>
                ) : (
                <>
                {wizardStep === 0 && (
                  <Stack spacing="20px" sx={{ width: "100%", px: 0 }}>

                    <Box
                      sx={{
                        width: "100%",
                        border: `1px solid ${FIELD_BORDER}`,
                        borderRadius: "12px",
                        p: "12px",
                        backgroundColor: colors.neutral[100],
                        position: "relative",
                      }}
                    >
                      <Stack spacing="16px" sx={{ width: "100%", minWidth: 0 }}>
                        <SafeTextField
                          fullWidth
                          sanitize={false}
                          label="Add website"
                          value={siteID}
                          disabled={fieldsLocked}
                          onChange={(e) => {
                            if (fieldsLocked) return;
                            const next = e.target.value;
                            setSiteID(next);
                            if (websiteTouched) applyWebsiteValidation(next);
                          }}
                          onFocus={() => {
                            if (fieldsLocked) return;
                            setFocusedField("website");
                          }}
                          onBlur={(e) => {
                            if (fieldsLocked) return;
                            setWebsiteTouched(true);
                            applyWebsiteValidation(e.target.value);
                            setFocusedField((prev) => (prev === "website" ? null : prev));
                          }}
                          inputProps={{ maxLength: 256 }}
                          error={Boolean(siteIDError)}
                          helperText={siteIDError || undefined}
                          inputRef={siteIDRef}
                        />
                        <SafeTextField
                          fullWidth
                          sanitize={false}
                          label="Origin (IP/FQDN)"
                          value={siteIP}
                          disabled={fieldsLocked}
                          onChange={(e) => {
                            if (fieldsLocked) return;
                            const next = e.target.value;
                            setSiteIP(next);
                            if (originTouched) applyOriginValidation(next);
                          }}
                          onFocus={() => {
                            if (fieldsLocked) return;
                            setFocusedField("origin");
                          }}
                          onBlur={(e) => {
                            if (fieldsLocked) return;
                            setOriginTouched(true);
                            applyOriginValidation(e.target.value);
                            setFocusedField((prev) => (prev === "origin" ? null : prev));
                          }}
                          inputProps={{ maxLength: 256 }}
                          error={Boolean(siteIPError)}
                          helperText={siteIPError || undefined}
                          inputRef={siteIPRef}
                        />
                        {showProjectSelect && (
                          <AddSiteProjectSelect
                            projectList={projectList}
                            value={projectId}
                            onChange={setProjectId}
                            disabled={fieldsLocked}
                          />
                        )}
                      </Stack>
                    </Box>
                    <TipBanner text={tipText} />
                  
                  </Stack>
                )}
                {wizardStep === 1 && (
                  <Stack spacing="20px" sx={{ width: "100%" }}>

                    <Box
                      sx={{
                        width: "100%",
                        border: `1px solid ${FIELD_BORDER}`,
                        borderRadius: "12px",
                        p: "12px",
                        backgroundColor: colors.neutral[100],
                        boxSizing: "border-box",
                      }}
                    >
                      <Stack spacing="12px" sx={{ width: "100%" }} role="radiogroup" aria-label="SSL mode">
                        <Box sx={{ display: "flex", gap: "13px", width: "100%", alignItems: "stretch" }}>
                          {SSL_TYPES.slice(0, 2).map((item) => (
                            <SslModeCard
                              key={`ssl_${item.type}`}
                              item={item}
                              selected={sslType === item.type}
                              onSelect={setSslType}
                              disabled={isOrgUser}
                            />
                          ))}
                        </Box>
                        <Box sx={{ display: "flex", gap: "13px", width: "100%", alignItems: "stretch" }}>
                          {SSL_TYPES.slice(2, 4).map((item) => (
                            <SslModeCard
                              key={`ssl_${item.type}`}
                              item={item}
                              selected={sslType === item.type}
                              onSelect={setSslType}
                              disabled={isOrgUser}
                            />
                          ))}
                        </Box>
                      </Stack>
                    </Box>
                    <Stack spacing="12px" sx={{ width: "100%" }}>
                      <TipBanner text={sslTips[0]} />
                      <TipBanner text={sslTips[1]} />
                    </Stack>
                  
                  </Stack>
                )}
                {wizardStep === 2 && (
                  <Stack spacing="12px" sx={{ width: "100%", minWidth: 0, boxSizing: "border-box" }}>
                  <Box
                    sx={{
                      width: "100%",
                      minWidth: 0,
                      display: "flex",
                      gap: "12px",
                      alignItems: "stretch",
                      flexWrap: { xs: "wrap", md: "nowrap" },
                      boxSizing: "border-box",
                    }}
                  >
                    <Stack
                      spacing="8px"
                      sx={{
                        width: { xs: "100%", md: "50%" },
                        minWidth: 0,
                        flex: "1 1 380px",
                        alignSelf: "stretch",
                      }}
                    >
                      <SslModeSummaryCard sslType={sslType} />
                      <Box
                        sx={{
                          width: "100%",
                          border: `1px solid ${FIELD_BORDER}`,
                          borderRadius: "12px",
                          p: "12px",
                          boxSizing: "border-box",
                          backgroundColor: colors.neutral[100],
                        }}
                      >
                        <Typography
                          sx={{ fontSize: 14, lineHeight: "18px", fontWeight: 500, color: TEXT_PRIMARY, mb: "12px" }}
                        >
                          Install SSL certificate
                        </Typography>
                        <Stack spacing="12px" role="radiogroup" aria-label="Install SSL certificate method">
                          {CERT_INSTALL_OPTIONS.map((option) => (
                            <CertInstallOption
                              key={option.value}
                              option={option}
                              selected={certType === option.value}
                              onSelect={setCertType}
                            />
                          ))}
                        </Stack>
                      </Box>
                      <Box
                        sx={{
                          width: "100%",
                          border: `1px solid ${FIELD_BORDER}`,
                          borderRadius: "12px",
                          px: "16px",
                          py: "12px",
                          boxSizing: "border-box",
                          backgroundColor: colors.neutral[100],
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "space-between",
                          gap: "12px",
                          mt: "auto",
                        }}
                      >
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                          <Typography sx={{ fontSize: 14, lineHeight: "18px", fontWeight: 500, color: TEXT_PRIMARY }}>
                            HTTP To HTTPS Redirection
                          </Typography>
                          <Typography
                            sx={{ fontSize: 12, lineHeight: "18px", fontWeight: 400, color: colors.neutral[700], mt: "2px" }}
                          >
                            Redirect all HTTP traffic to HTTPS.
                          </Typography>
                        </Box>
                        <GreenSwitch
                          checked={httpsRedirectEnabled}
                          onChange={(e) => {
                            httpsRedirectTouchedRef.current = true;
                            setHttpsRedirectEnabled(e.target.checked);
                          }}
                          disabled={isOrgUser}
                          inputProps={{ "aria-label": "HTTP to HTTPS redirection" }}
                        />
                      </Box>
                    </Stack>
                    <Box
                      sx={{
                        width: { xs: "100%", md: "50%" },
                        minWidth: 0,
                        flex: "1 1 380px",
                        alignSelf: "stretch",
                        display: "flex",
                        overflow: "hidden",
                        ...revealChildScrollbarsOnHoverSx,
                      }}
                    >
                      <InstallSslRightPanel
                        certType={certType}
                        uploadFullchain={uploadFullchain}
                        setUploadFullchain={setUploadFullchain}
                        uploadPrivkey={uploadPrivkey}
                        setUploadPrivkey={setUploadPrivkey}
                        cnameValidations={cname_validations}
                        onVerifyDomain={handleVerifyFreeDomain}
                        verifyLoading={freeCertLoading}
                        sslCertificateReady={installSslCertificateReady}
                        originCert={originCert}
                        onGenerateOrigin={handleGenerateOriginCert}
                        originLoading={originCertLoading}
                        originCertificateReady={installOriginCertificateReady}
                        universalDomainVerified={universalDomainVerified}
                        mutateDisabled={isOrgUser}
                        universalActionsDisabled={false}
                        freeCertError={freeCertError}
                        onRetryGenerate={handleRetryFreeGenerate}
                        continueHint={installContinueHint}
                      />
                    </Box>
                  </Box>
                  <TipBanner text={installTips[0]} />
                  <TipBanner text={installTips[1]} />
                  </Stack>
                )}
                {wizardStep === 3 && (
                  <Stack spacing="20px" sx={{ width: "100%" }}>
                    <Box
                      sx={{
                        width: "100%",
                        display: "flex",
                        gap: "12px",
                        alignItems: "stretch",
                        flexWrap: { xs: "wrap", md: "nowrap" },
                      }}
                    >
                      <WafToggleCard
                        title="Layerd AI Smart Parse"
                        description="AI-powered request parsing"
                        enabled={smartParseActive}
                        onToggle={(e) => setSmartParseActive(e.target.checked)}
                        Icon={GlobalIcon}
                        disabled={!canConfigureWAF}
                      />
                      <WafToggleCard
                        title="Signature WAF"
                        description="Layerd Signature Detection"
                        enabled={signatureWafActive}
                        onToggle={(e) => setSignatureWafActive(e.target.checked)}
                        Icon={SignaturewafIcon}
                        disabled={!canConfigureWAF}
                      />
                    </Box>
                    <Stack spacing="12px" sx={{ width: "100%" }}>
                      <TipBanner text={wafTipPrimary} />
                      <TipBanner text={WAF_TIP_LAYERED} />
                    </Stack>
                  </Stack>
                )}
                {wizardStep === 4 && (
                  <Stack
                    direction={{ xs: "column", md: "row" }}
                    spacing={{ xs: "16px", md: "24px" }}
                    alignItems={{ xs: "stretch", md: "center" }}
                    sx={{ width: "100%" }}
                    aria-label="Configuration process checklist"
                  >
                    <Stack spacing="12px" sx={{ flex: 1, minWidth: 0, width: "100%" }}>
                      {CONFIG_PROCESS_STEPS.map((label, index) => {
                        const status = configProcessFetchFailed
                          ? ConfigStepStatus.PENDING
                          : getConfigProcessStepStatus(index);
                        return (
                          <ConfigProcessRow key={`cfg_${index}`} index={index} label={label} status={status} />
                        );
                      })}
                      {configProcessFetchFailed && (
                        <Stack spacing="12px" sx={{ width: "100%" }}>
                          <TipBanner text="Failed to load SSL or WAF configuration. You can retry, or Continue to Review." />
                          <Button
                            onClick={handleConfigProcessRetry}
                            sx={{ ...outlinedButtonSx, height: 36, alignSelf: "flex-start" }}
                          >
                            Retry configuration load
                          </Button>
                        </Stack>
                      )}
                      {configProcessError && !configProcessFetchFailed && (
                        <TipBanner text="Certificate install did not complete. Go Back to Install SSL, or Continue to Review." />
                      )}
                    </Stack>
                    <Box
                      sx={{
                        width: { xs: 160, md: 180 },
                        height: { xs: 160, md: 180 },
                        position: "relative",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        flexShrink: 0,
                        alignSelf: "center",
                      }}
                    >
                      <CircularProgress
                        variant="determinate"
                        value={100}
                        size={160}
                        thickness={4}
                        sx={{ color: colors.neutral[200], position: "absolute" }}
                      />
                      <CircularProgress
                        variant="determinate"
                        value={configProcessFetchFailed ? 0 : configProcessPercent}
                        size={160}
                        thickness={4}
                        sx={{
                          color: configProcessAnimComplete ? colors.green[300] : colors.primary[300],
                          position: "absolute",
                          "& .MuiCircularProgress-circle": { strokeLinecap: "round" },
                        }}
                      />
                      <Box
                        sx={{
                          position: "absolute",
                          display: "flex",
                          flexDirection: "column",
                          alignItems: "center",
                          justifyContent: "center",
                        }}
                      >
                        {configProcessAnimComplete && !configProcessFetchFailed ? (
                          <DoneIcon style={{ width: 40, height: 40, color: colors.green[300] }} />
                        ) : (
                          <>
                            <Typography
                              sx={{
                                fontSize: 28,
                                lineHeight: "34px",
                                fontWeight: 700,
                                color: colors.primary[300],
                              }}
                            >
                              {configProcessFetchFailed ? 0 : configProcessPercent}%
                            </Typography>
                            <Typography sx={{ fontSize: 12, lineHeight: "18px", color: colors.neutral[600] }}>
                              Deploying
                            </Typography>
                          </>
                        )}
                      </Box>
                    </Box>
                  </Stack>
                )}
                {wizardStep === 5 && (
                  <Stack spacing="12px" sx={{ width: "100%" }} aria-label="Review configuration summary">
                    <ReviewSectionCard>
                      <ReviewFieldCell
                        fullWidth
                        icon={<ReviewSvgIcon Icon={WebIcon} />}
                        label="Domain Name"
                      >
                        <ReviewTextValue value={siteID} />
                      </ReviewFieldCell>
                      <ReviewPairRow>
                        <ReviewFieldCell icon={<ReviewSvgIcon Icon={ServerIcon} />} label="Origin name/IP">
                          <ReviewTextValue value={siteIP} />
                        </ReviewFieldCell>
                        <ReviewFieldCell icon={<ReviewSvgIcon Icon={CertIcon} />} label="Certificate Type">
                          <ReviewTextValue value={certTypeLabelForReview} />
                        </ReviewFieldCell>
                      </ReviewPairRow>
                      <ReviewPairRow>
                        <ReviewFieldCell icon={<ReviewSvgIcon Icon={CertIcon} />} label="SSL Status">
                          <WafStatusBadge enabled={sslEnabledForReview} />
                        </ReviewFieldCell>
                        <ReviewFieldCell icon={<ReviewSvgIcon Icon={RedirectIcon} />} label="HTTPS Redirection">
                          <WafStatusBadge enabled={httpsRedirectEnabled} />
                        </ReviewFieldCell>
                      </ReviewPairRow>
                      <ReviewPairRow>
                        <ReviewFieldCell icon={<ReviewSvgIcon Icon={SignaturewafIcon} />} label="Signature WAF">
                          <WafStatusBadge enabled={signatureWafActive} />
                        </ReviewFieldCell>
                        <ReviewFieldCell
                          icon={<ReviewSvgIcon Icon={GlobalIcon} />}
                          label="Layerd AI Smart Parse"
                        >
                          <WafStatusBadge enabled={smartParseActive} />
                        </ReviewFieldCell>
                      </ReviewPairRow>
                    </ReviewSectionCard>

                    <ReviewSectionCard>
                      <ReviewFieldCell
                        fullWidth
                        icon={<ReviewMuiIcon Icon={DnsOutlinedIcon} />}
                        label="Layerd Anycast IP Addresses"
                      >
                        <ReviewCopyValue
                          value={edgeConfig?.edge_a_records?.[0]}
                          loading={edgeConfigLoading}
                          ariaLabel="Copy Layerd Anycast IP"
                        />
                      </ReviewFieldCell>
                      <ReviewFieldCell
                        fullWidth
                        icon={<ReviewMuiIcon Icon={LinkOutlinedIcon} />}
                        label="Layerd CNAME Record"
                      >
                        <ReviewCopyValue
                          value={edgeConfig?.edge_cname_records?.[0]}
                          loading={edgeConfigLoading}
                          ariaLabel="Copy Layerd CNAME record"
                        />
                      </ReviewFieldCell>
                    </ReviewSectionCard>

                    <TipBanner text={REVIEW_TIP_WWW_CNAME} />
                    <TipBanner text={REVIEW_TIP_MULTI_SUBDOMAIN} />
                  </Stack>
                )}
                </>
                )}
                </Box>
              </Box>
            </Box>
            </Box>
          </Stack>

          <Box
            sx={{
              display: "flex",
              justifyContent: "flex-end",
              alignItems: "center",
              gap: "12px",
              width: "100%",
              maxWidth: "100%",
              minWidth: 0,
              mt: "20px",
              flexWrap: "wrap",
              flexShrink: 0,
              boxSizing: "border-box",
            }}
          >
            {wizardStep > 0 && (
              <Button
                onClick={handleWizardBack}
                disabled={anyStepSubmitting || isStepAnimating}
                startIcon={<ArrowleftIcon style={{ color: colors.primary[300] }} />}
                sx={{ ...outlinedButtonSx, height: 36, px: 4 }}
              >
                Back
              </Button>
            )}
            {wizardStep === 0 ? (
              <Button
                onClick={handleWebsiteNext}
                disabled={isSubmitting || isStepAnimating}
                endIcon={
                  isSubmitting ? (
                    <CircularProgress size={16} sx={{ color: "#fff" }} />
                  ) : (
                    <ArrowrightIcon style={{ color: "#fff" }} />
                  )
                }
                sx={{ ...containedButtonSx, height: 36, px: 4 }}
              >
                {primaryCtaLabel}
              </Button>
            ) : wizardStep === 1 ? (
              <Button
                onClick={handleSslNext}
                disabled={isStepAnimating || isSslSubmitting}
                endIcon={
                  isSslSubmitting ? (
                    <CircularProgress size={16} sx={{ color: "#fff" }} />
                  ) : (
                    <ArrowrightIcon style={{ color: "#fff" }} />
                  )
                }
                sx={{ ...containedButtonSx, height: 36, px: 4 }}
              >
                {primaryCtaLabel}
              </Button>
            ) : wizardStep === 2 ? (
              <Button
                onClick={handleInstallSslNext}
                disabled={isStepAnimating || isInstallSubmitting || installContinueDisabled}
                aria-describedby={installContinueHint ? "ad2-install-continue-blocked" : undefined}
                endIcon={
                  isInstallSubmitting ? (
                    <CircularProgress size={16} sx={{ color: "#fff" }} />
                  ) : (
                    <ArrowrightIcon style={{ color: "#fff" }} />
                  )
                }
                sx={{ ...containedButtonSx, height: 36, px: 4 }}
              >
                {primaryCtaLabel}
              </Button>
            ) : wizardStep === 3 ? (
              <Button
                onClick={handleWafNext}
                disabled={isStepAnimating || isWafSubmitting}
                endIcon={
                  isWafSubmitting ? (
                    <CircularProgress size={16} sx={{ color: "#fff" }} />
                  ) : (
                    <ArrowrightIcon style={{ color: "#fff" }} />
                  )
                }
                sx={{ ...containedButtonSx, height: 36, px: 4 }}
              >
                {primaryCtaLabel}
              </Button>
            ) : wizardStep === 4 ? (
              <Button
                onClick={handleConfigProcessNext}
                disabled={isStepAnimating || !configProcessContinueEnabled}
                endIcon={<ArrowrightIcon style={{ color: "#fff" }} />}
                sx={{ ...containedButtonSx, height: 36, px: 4 }}
              >
                {primaryCtaLabel}
              </Button>
            ) : (
              <Button
                onClick={handleReviewFinish}
                disabled={isStepAnimating || isFinishSubmitting}
                endIcon={
                  isFinishSubmitting ? (
                    <CircularProgress size={16} sx={{ color: "#fff" }} />
                  ) : (
                    <ArrowrightIcon style={{ color: "#fff" }} />
                  )
                }
                sx={{ ...containedButtonSx, height: 36, px: 4 }}
              >
                {primaryCtaLabel}
              </Button>
            )}
          </Box>
        </Box>
      </Fade>
    </Modal>
    <UnsavedChangesConfirmModal
      open={!!mcpImpact}
      onCancel={() => setMcpImpact(null)}
      onContinue={() => {
        mcpImpactAckRef.current = true;
        setMcpImpact(null);
        handleWebsiteNext();
      }}
      title="MCP endpoints will stop being protected"
      body={
        <>
          {`Onboarding ${mcpImpact?.site_id || "this hostname"} makes it the owner of the hostname${
            (mcpImpact?.endpoints?.length || 0) > 1 ? "s" : ""
          } below. ${(mcpImpact?.endpoints?.length || 0) > 1 ? "These MCP endpoints belong" : "This MCP endpoint belongs"} to another site, so MCP protection for ${
            (mcpImpact?.endpoints?.length || 0) > 1 ? "them" : "it"
          } stops as soon as the site is created and traffic falls back to the site's normal WAF rules.`}
          <Box component="span" sx={{ display: "block", my: 1.5 }}>
            {(mcpImpact?.endpoints || []).map((e) => (
              <Box component="span" key={e.endpoint_id} sx={{ display: "block", pl: 1 }}>
                • <b>{e.location_pattern}</b> — on site {e.site_id}
              </Box>
            ))}
          </Box>
          To keep MCP protection, recreate the endpoint on the new site after onboarding.
        </>
      }
      cancelLabel="Cancel"
      continueLabel="Onboard anyway"
    />
    <UnsavedChangesConfirmModal
      open={discardConfirmOpen}
      onCancel={handleDiscardCancel}
      onContinue={handleDiscardConfirm}
      title={leaveConfirmTitle}
      body={leaveConfirmBody}
      cancelLabel={leaveConfirmCancelLabel}
      continueLabel={leaveConfirmContinueLabel}
    />
    </>
  );
};

export default AddDomainWebsiteInformationModal;
